import { hex } from "../shared/color";
import { CELL, COIN_CLOWN, COIN_DIGGER, COIN_HEX, COIN_IRON, COIN_NOVA, GRAVITY, SOLID } from "../shared/defs";
import type { LetterBody, Letters, Particles } from "./entities";
import { PUNCH, Punchline } from "./punchline";
import { SCALE } from "./rig";
import { BOSS_BAR_OFFSET } from "./screenbox";
import { gateTitle, type GateAffix } from "./portal";
import { heroFx } from "./skins";
import { segBox, type Slab, type Slabs } from "./slabs";
import type { World } from "./world";

/**
 * Challenge combat. Minion HP stays 52 so a pistol is about five hits.
 * SMG damage is a short burst. A close shotgun lands enough pellets to kill in one or two shots.
 * A rocket or grenade that strikes a body deals full damage, so a normal minion dies.
 * Splash on other bodies still falls off. The boss has 300 HP, so it takes four rockets.
 */
export const MINION_HP = 52;
export const BOSS_HP = 300;
/** Journey elite baselines before pressure toughness. */
export const ELITE_TANK_HP = 240;
export const ELITE_SUMMONER_HP = 150;
export const ELITE_RUSHER_HP = 110;
/** Journey floor-boss baselines (Bastion / Nest / Rampage). */
export const GATE_BOSS_TANK_HP = 480;
export const GATE_BOSS_SUMMONER_HP = 380;
export const GATE_BOSS_RUSHER_HP = 320;
export const ELITE_KILL_COINS = 4;
export const GATE_BOSS_KILL_COINS = 10;
export const GATE_BOSS_GEM = 10;
export const ADD_HP = 22;
export const PISTOL_DMG = 11;
export const SMG_DMG = 10;
export const SHOTGUN_DMG = 20;
export const DRONE_SHOT_DMG = 8;
export const RAIL_DMG = 52;
export const FLAME_DPS = 110;
export const ROCKET_DMG = 88;
export const GRENADE_DMG = 88;
export const BOMBLET_DMG = 52;
export const DRONE_BLAST_DMG = 72;
export const MINION_KNOCK = 120;
export const BOSS_KNOCK = 42;

export function minionShotDmg(weapon: string): number {
  switch (weapon) {
    case "pistol": return PISTOL_DMG;
    case "smg": return SMG_DMG;
    case "shotgun": return SHOTGUN_DMG;
    case "drone": return DRONE_SHOT_DMG;
    case "railgun": return RAIL_DMG;
    default: return PISTOL_DMG;
  }
}

export function minionBlastDmg(kind: string): number {
  switch (kind) {
    case "rocket": return ROCKET_DMG;
    case "grenade": return GRENADE_DMG;
    case "overkill": return BOMBLET_DMG;
    case "drone": return DRONE_BLAST_DMG;
    default: return ROCKET_DMG;
  }
}

const COIN_KEY = "daw:coins";
/**
 * destruction() on a tall page fills from the top: half popped glyphs (capped
 * at 2400) and half removed cells (capped at 120000). Loot blocks are placed in
 * that same vertical span, 14–18 of them, 3 coins each, plus one gem worth 6
 * (48–60). Sparse pages pay up to 6 per cache. Those drops count toward
 * PICKUP_BUDGET. Ordinary destruction then tops the purse up to
 * round(PICKUP_BUDGET * fraction), so missing the caches still follows the meter.
 * CLEAR_BONUS is +10 / +15 / +10 / +45, once per page, solo Sandbox and Challenge.
 * That is 25 at 60%, 35 at 75%, and 80 at 100%.
 * 60% → round(0.6 * 86) + 25 = 77, and at most ~85 if every cache opens early.
 * 80% → round(0.8 * 86) + 35 = 104.
 * 100% → 86 + 80 = 166.
 * A boss kill is extra: 4 when the block breaks, then 6 coins and a gem worth 8.
 * Minions pay 1. Prices stay 200 / 400 / 600.
 * Coins inside COIN_MAGNET ease into the player. Once pulled, they collect on the
 * body or inside COIN_GRAB, and the top-of-page clamp does not apply. They last
 * COIN_LIFE seconds and blink for the last COIN_BLINK. A gem, or Magnus's
 * magnet while it is out, pulls from COIN_VACUUM instead.
 */
const LOOT_MIN = 14;
const LOOT_EXTRA = 5;
const LOOT_VALUE = 3;
const LOOT_GEM = 6;
const PICKUP_BUDGET = 86;
const BOSS_BREAK_COINS = 4;
const BOSS_KILL_COINS = 6;
const BOSS_GEM = 8;
const MINION_COINS = 1;
/**
 * Journey only. Paid once when the player steps through the portal after every
 * monster that actually spawned on that page is dead. About a quarter of a
 * typical page (the HUD calls that about 100 coins; the portal usually opens
 * nearer 85%, which is about 108 before monster pocket change).
 */
export const PORTAL_FULL_CLEAR_COINS = 25;
const COIN_LIFE = 40;
const COIN_BLINK = 5;
const COIN_MAGNET = 128;
const COIN_VACUUM = 220;
/** Once the magnet has a coin, collect at the body or inside this radius. */
const COIN_GRAB = 36;

/**
 * Magnus magnet. One place for the tuning.
 * A rocket does not stop on the page. It flies to the cursor and deals 150 to every
 * block its radius-46 carve opens, plus 90 splash out to 46·1.8 ≈ 83. On a real page
 * the destruction percent is whole elements detaching, not the disc of missing pixels.
 * Carve radii are world pixels, the same space as ROCKET_R. Zoom only changes the render scale.
 *
 * The clump is a heavy slug. It flies to the cursor (at least MAGNET_PAST px past the
 * first bite) and bores a trench on the way, unless it has already chewed through
 * 60–120px of material (by weight, measured from the cells the trench removes).
 * Then it detonates. The trench hits the first block it opens for MAGNET_BORE_DMG
 * (enough to pop a button or a short bar on any page) and nicks the next two for
 * MAGNET_BORE_NEXT, so real blocks wait for the blast. The blast itself matches a
 * rocket's direct hit (radius 46, damage 150, splash 90 out to 46·1.8 ≈ 83), so a
 * clump always opens what a rocket would.
 * A page-wide band (area above MAGNET_WIDE_AREA) takes no more than one rocket hit
 * (MAGNET_WIDE_TOTAL) from the whole throw, trench, blast and scrap together.
 * On top of that, a full clump drives a shockwave on along its flight: the
 * MAGNET_FURROW_N nearest glyphs ahead of the burst (within MAGNET_FURROW_D) are
 * knocked off, fewer when the clump was thrown uphill, and up to MAGNET_FURROW_POP
 * small elements ahead pop off. Nothing here depends on where on the page the
 * burst lands. Scrap shards only scuff blocks, so a throw does not swing on where
 * its shards happen to fall.
 * A quick tap (a press under MAGNET_TAP_HOLD) takes the tap path: it rips one or two
 * pieces, bores at most 96px, still capped by the aim point, with a narrow trench
 * (damage 28), and bursts like a rocket (radius 46, damage 150, splash 90 out to
 * 46·1.8 ≈ 83). Trench damage counts toward that burst, so no block takes more
 * than a rocket would deal it.
 */
export const MAGNET_RANGE = 228;
export const MAGNET_HALF_DEG = 42;
export const MAGNET_RIP = 0.3;
export const MAGNET_RIP_PER_TICK = 5;
/** Cook time lost per second while a block is outside the cone. */
export const MAGNET_DECAY = 0.32;
/** Attached glyphs torn loose per second while the cone is held. */
export const MAGNET_GLYPH_RATE = 14;
export const MAGNET_CD = 1.35;
export const MAGNET_HOLD_MAX = 3.4;
export const MAGNET_CAP = 18;
export const MAGNET_AREA_CAP = 28000;
export const MAGNET_MASS_FULL = 4.8;
export const MAGNET_SLOW = 0.76;
export const MAGNET_PULL = 22;
export const MAGNET_R0 = 46;
/** Carve radius of a full clump. The ring and dust stay on MAGNET_FX_R1. */
export const MAGNET_R1 = 46;
/** Impact ring, dust, and scrap-hole placement. Unchanged from the passed effect. */
export const MAGNET_FX_R1 = 54;
export const MAGNET_DMG0 = 140;
export const MAGNET_DMG1 = 150;
export const MAGNET_SPLASH0 = 74;
export const MAGNET_SPLASH1 = 90;
/** Splash reach as a multiple of carve radius. Rocket uses 1.8 on radius 46 (≈ 83px). */
export const MAGNET_SPLASH_MUL = 2.1;
/** How far a clump keeps moving after it bites the page, in px of solid material. */
export const MAGNET_PEN0 = 60;
export const MAGNET_PEN1 = 120;
/**
 * Minimum travel after the bite, in px. The cursor may extend this, never shorten it.
 * A tap is not included: its aim point still ends the bore.
 */
export const MAGNET_PAST = 60;
/** Trench radius while that slug is inside the page. */
export const MAGNET_BORE0 = 18;
export const MAGNET_BORE1 = 20;
/** Trench hit on the first block the slug opens. Pops a button or short bar, not a card. */
export const MAGNET_BORE_DMG = 80;
/** Nick for each of the next two blocks. Later blocks take nothing from the trench. */
export const MAGNET_BORE_NEXT = 60;
export const MAGNET_BORE_MORE = 2;
/**
 * Running total on one page-wide band for this throw: one rocket hit. A band a
 * rocket breaks still breaks; a band that survives a rocket survives the clump.
 */
export const MAGNET_WIDE_TOTAL = 150;
/** A block larger than this (px²) counts as a page-wide band. */
export const MAGNET_WIDE_AREA = 40_000;
/**
 * A band larger than this (a desktop hero or a big card holding much of the page)
 * takes at most MAGNET_HUGE_TOTAL from one throw and is never finished by a throw,
 * so one throw never drops a third of the page at once. Other weapons finish it,
 * and enough carving still knocks it loose.
 */
export const MAGNET_HUGE_AREA = 120_000;
export const MAGNET_HUGE_TOTAL = 60;
/**
 * A page-wide band whose box is farther than this from the blast center
 * only takes a nick. A center inside the band still takes the full capped hit,
 * so a hero is not deleted by a blast that merely clips its edge.
 */
export const MAGNET_WIDE_GAP = 28;
/** Full-clump letter reach is radius × this, the same as a rocket. */
export const MAGNET_LETTER_MUL = 1.9;
/**
 * Shockwave of a full clump: the MAGNET_FURROW_N nearest glyphs ahead of the
 * burst (along the flight direction, outside the blast reach, within
 * MAGNET_FURROW_D px) are knocked off. Glyphs off to the side count as
 * farther by MAGNET_FURROW_SIDE, so the wave reads as a forward cone.
 */
export const MAGNET_FURROW_D = 900;
export const MAGNET_FURROW_N = 50;
export const MAGNET_FURROW_SIDE = 1.2;
/**
 * Gravity on the shockwave: a clump thrown uphill has lost some of its punch and
 * knocks off fewer glyphs. Count = MAGNET_FURROW_N · (1 − this · up), where up is
 * the flight direction's upward component (0 level or downhill, 1 straight up).
 */
export const MAGNET_FURROW_FALL = 0.4;
/**
 * The shockwave also pops up to MAGNET_FURROW_POP small elements ahead
 * (buttons, icons, short bars no larger than MAGNET_FURROW_POP_AREA px²,
 * within MAGNET_FURROW_POP_D px), ranked the same way as the glyphs.
 */
export const MAGNET_FURROW_POP = 2;
export const MAGNET_FURROW_POP_AREA = 6000;
export const MAGNET_FURROW_POP_D = 320;
/** Stored beside dealt damage in the wide-block map. Damage itself stays below this. */
const WIDE_NICK = 1 << 16;
export const MAGNET_SPEED0 = 1120;
export const MAGNET_SPEED1 = 860;
export const MAGNET_LOFT = 70;
/** Flight gravity. World gravity is 1900; a heavy clump still arcs, but it reaches the aim. */
export const MAGNET_GRAV0 = 980;
export const MAGNET_GRAV1 = 480;
export const MAGNET_SAT_N = 5;
export const MAGNET_SAT_R = 9;
export const MAGNET_SAT_DMG = 28;
/**
 * A press shorter than this is a quick tap. If the cone has only caught a few
 * glyphs by then (weight under MAGNET_TAP_WEIGHT), the tap path throws them.
 */
export const MAGNET_TAP_HOLD = 0.22;
export const MAGNET_TAP_WEIGHT = 0.25;
export const MAGNET_TAP_R = 46;
export const MAGNET_TAP_DMG = 150;
export const MAGNET_TAP_SPLASH = 90;
/** Tap splash reach is 46·1.8 ≈ 83, the rocket's. */
export const MAGNET_TAP_SPLASH_MUL = 2.1;
/** A tap still reaches a cursor that sits about 80px inside the page. */
export const MAGNET_TAP_PEN = 96;
export const MAGNET_TAP_BORE = 8;
/** Under the ~68 HP of a small bar, so the tap trench does not pop it. */
export const MAGNET_TAP_BORE_DMG = 28;
export const MAGNET_TAP_SPEED = 560;
export const MAGNET_TAP_GRAV = 720;
export const MAGNET_SHARD_R = 6;
export const MAGNET_SHARD_DMG = 14;
/**
 * Block damage of one scrap shard. Kept to a scuff: shards fly at random, so
 * real damage here made the same throw swing by a whole picture.
 */
export const MAGNET_SHARD_BLOCK = 3;
/** Body damage of one shard, and the most all shards of one throw deal to bodies together. */
export const MAGNET_SHARD_HURT = 36;
export const MAGNET_SHARD_HURT_CAP = 40;
/**
 * Body damage of a full clump's core (weight 1). Lighter clumps blend down to the
 * speed-based hit. With the shard cap a full clump deals about 170 to a boss, ~1.9 rockets.
 */
export const MAGNET_HIT_FULL = 130;
export const MAGNET_SHAKE0 = 0.48;
export const MAGNET_SHAKE1 = 1.05;
/** Impact damage to a minion is speed × this, clamped. A full throw is about 860 px/s → 86. */
export const MAGNET_MINION_SCALE = 0.1;
const MAGNET_HALF = (MAGNET_HALF_DEG * Math.PI) / 180;
/** Paid the first time destruction crosses each mark. The last mark matches a full clear. */
const CLEAR_BONUS = [
  { at: 0.25, coins: 10 },
  { at: 0.5, coins: 15 },
  { at: 0.75, coins: 10 },
  { at: 1, coins: 45 },
] as const;

export const HEROES = [
  { id: "stickman", name: "Stickman", cost: 0, weapon: "" },
  { id: "iron", name: "Magnus", cost: COIN_IRON, weapon: "magnet" },
  { id: "digger", name: "Digger", cost: COIN_DIGGER, weapon: "drill" },
  { id: "clown", name: "Clown", cost: COIN_CLOWN, weapon: "punchline" },
  { id: "hex", name: "Hex", cost: COIN_HEX, weapon: "meteor" },
  { id: "nova", name: "Nova", cost: COIN_NOVA, weapon: "tesla" },
] as const;

export type HeroId = (typeof HEROES)[number]["id"];

export function readCoins(): number {
  return Math.max(0, Math.floor(Number(localStorage.getItem(COIN_KEY)) || 0));
}

export function writeCoins(n: number): number {
  const v = Math.max(0, Math.floor(n));
  localStorage.setItem(COIN_KEY, String(v));
  return v;
}

export function ownedHero(id: string): boolean {
  const hero = HEROES.find((item) => item.id === id);
  if (!hero || hero.cost <= 0) return true;
  return localStorage.getItem(`daw:own:${id}`) === "1";
}

export function readHero(): HeroId {
  const id = localStorage.getItem("daw:hero") || "stickman";
  return HEROES.some((item) => item.id === id) && ownedHero(id) ? (id as HeroId) : "stickman";
}

export function writeHero(id: string) {
  localStorage.setItem("daw:hero", id);
}

export function markOwned(id: string) {
  localStorage.setItem(`daw:own:${id}`, "1");
}

export type PlayMode = "sandbox" | "challenge" | "journey";

/** Saved `daw:mode` wins. With none saved, `daw:fight=off` is still Sandbox; otherwise Journey. */
export function playModeFrom(saved: string | null, fight: string | null): PlayMode {
  if (saved === "sandbox" || saved === "challenge" || saved === "journey") return saved;
  if (fight === "off") return "sandbox";
  return "journey";
}

export function readPlayMode(): PlayMode {
  return playModeFrom(localStorage.getItem("daw:mode"), localStorage.getItem("daw:fight"));
}

export function writePlayMode(mode: PlayMode) {
  localStorage.setItem("daw:mode", mode);
  localStorage.setItem("daw:fight", mode === "sandbox" ? "off" : "on");
}

export function fightOn(): boolean {
  return readPlayMode() !== "sandbox";
}

export function setFight(on: boolean) {
  writePlayMode(on ? "challenge" : "sandbox");
}

export interface FightHost {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly aim: number;
  /** Cursor in world pixels. A full throw bores toward this, and past it when the cursor is closer than MAGNET_PAST. */
  readonly lookX?: number;
  readonly lookY?: number;
  readonly facing: number;
  readonly flaming: boolean;
  readonly online: boolean;
  readonly world: World;
  readonly letters: Letters;
  readonly chunks: Slabs;
  readonly parts: Particles;
  destruction(): number;
  knock(ix: number, iy: number): void;
  sfx(id: string, pitch?: number): void;
  /** Journey contact. Returns false when the player is shielded or already down. */
  sting?(amount: number): boolean;
  /** Journey hearts. Returns false when health is already full. */
  heal?(amount: number): boolean;
  /** A coin just entered the purse. `x`/`y` are world pixels. */
  onPickup?(value: number, x: number, y: number, gem: boolean): void;
  shake(n: number): void;
  markShot(): void;
  /** Freeze the world for a few frames after a heavy magnet impact. */
  freeze(seconds: number): void;
  /** Glowing rim, screen pulse, and metal sparks for a magnet impact. Jagged arcs are drawn by the fight. */
  bloom?(x: number, y: number, radius: number, weight: number): void;
  hand(): { x: number; y: number };
  peekHand(): { x: number; y: number };
  aimAt(): { x: number; y: number };
  flash(amount: number): void;
  glow?(x: number, y: number, radius: number, strength: number, life: number): void;
}

interface Coin {
  x: number;
  y: number;
  vx: number;
  vy: number;
  gem: boolean;
  value: number;
  life: number;
  spin: number;
}

interface Heart {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  spin: number;
}

type MagnetKind = "slab" | "letter" | "coin" | "minion";

interface MagnetBit {
  kind: MagnetKind;
  x: number;
  y: number;
  a: number;
  ox: number;
  oy: number;
  w: number;
  h: number;
  mass: number;
  area: number;
  spin: number;
  slab?: Slab;
  letter?: LetterBody;
  coin?: Coin;
  body?: Body;
}

interface MagnetToss {
  x: number;
  y: number;
  vx: number;
  vy: number;
  a: number;
  av: number;
  bits: MagnetBit[];
  weight: number;
  dmg: number;
  splash: number;
  radius: number;
  age: number;
  bounces: number;
  grav: number;
  /** Full clump only. Immediate scrap holes around the core. */
  sats: boolean;
  tap: boolean;
  aimX: number;
  aimY: number;
  /** Px of flight left. A full throw never starts this below MAGNET_PAST. */
  aimLeft: number;
  /** Px of solid material this slug will cross. 0 until it bites. */
  pen: number;
  /** Solid px already crossed. */
  dug: number;
  digging: boolean;
  /** Blocks the trench has already counted, in path order. */
  met: Set<number>;
  /** How many blocks the trench has counted. The first takes a full hit. */
  struck: number;
  /** Blocks the trench already damaged, so the blast does not hit them again. */
  bored: Set<number>;
  /**
   * Damage already dealt to each page-wide band. A grazed band is flagged so
   * later scrap and shards cannot raise it above MAGNET_BORE_NEXT.
   */
  wide: Map<number, number>;
  /**
   * Tap only: trench damage per block. The tap burst only tops a block up to its
   * own damage, so a nicked block ends up with what a rocket would deal, not more.
   */
  cut: Map<number, number>;
}

interface MagnetArc {
  x: number;
  y: number;
  life: number;
  max: number;
  r0: number;
  r1: number;
  weight: number;
}

interface MagnetShard {
  bit: MagnetBit;
  vx: number;
  vy: number;
  av: number;
  age: number;
  boomed: boolean;
  /** Shared with the toss so a shard cannot finish a page-wide band. */
  wide?: Map<number, number>;
  /** Shared by every shard of one throw: body damage the shards may still deal. */
  hurt: { left: number };
}

interface Body {
  x: number;
  y: number;
  vx: number;
  vy: number;
  a: number;
  av: number;
  w: number;
  h: number;
  canvas: HTMLCanvasElement;
  bx: number;
  by: number;
  bw: number;
  bh: number;
  hp: number;
  maxHp: number;
  kind: "hop" | "crawl" | "fly" | "boss" | "thrown";
  eyes: boolean;
  hop: number;
  slam: number;
  wind: number;
  /** Journey only. Seconds left backing away after this body landed a hit. */
  shy: number;
  flash: number;
  age: number;
  purse: number;
  hit: number;
  phase: "telegraph" | "peel" | "land" | "roar" | "chase";
  phaseT: number;
  tele: number;
  bar: number;
  sx: number;
  sy: number;
  blink: number;
  shut: number;
  homeX: number;
  homeY: number;
  homeW: number;
  homeH: number;
  title: string;
  /** Punchline threw this body. Landing carves; magnet throws leave this false. */
  launched: boolean;
  dug: boolean;
  /** Seconds of punch knockback before chase steering resumes. */
  shoveT: number;
  /** This body woke up as a monster on this page. Thrown debris stays false. */
  born: boolean;
  countedDead: boolean;
  /** Journey gate foe or ARPG-style elite/boss retrofit. Empty for trash. */
  affix: GateAffix | "";
  /** Must die before the Journey portal may open. */
  gate: boolean;
  /** Half-HP phase two for gate elites/bosses. */
  phase2: boolean;
  /** Tank short shield timer (damage reduction while > 0). */
  shieldT: number;
  /** Summoner / Nest: seconds until the next add wave. */
  skillCd: number;
  /** Rusher / Rampage: telegraph wind-up before a charge. */
  chargeWind: number;
  chargeDirX: number;
  chargeDirY: number;
  /** Active charge dash timer. */
  chargeT: number;
  /** Living adds owned by a summoner (soft DR while > 0). */
  adds: number;
}

interface Stir {
  index: number;
  x: number;
  y: number;
  w: number;
  h: number;
  t: number;
  dur: number;
  kind: Body["kind"];
  purse: number;
  dust: number;
  cracked: number;
}

interface Scar {
  x: number;
  y: number;
  w: number;
  h: number;
  life: number;
  max: number;
}

interface Floater {
  x: number;
  y: number;
  vy: number;
  life: number;
  n: number;
  plus: boolean;
  color: string;
  icon?: "coin" | "gem";
  /** Player damage, hearts' +15, and monster damage. About 1.5× with an outline. */
  big?: boolean;
}

interface Burst {
  x: number;
  y: number;
  life: number;
  max: number;
  boss: boolean;
}

interface BreakBlock {
  x: number;
  y: number;
  w: number;
  h: number;
}

function hashUrl(url: string): number {
  let h = 2166136261;
  for (let i = 0; i < url.length; i++) h = Math.imul(h ^ url.charCodeAt(i), 16777619);
  return h >>> 0;
}

function mix(seed: number, i: number): number {
  let h = Math.imul(seed ^ (i + 1), 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  return (h ^ (h >>> 13)) >>> 0;
}

function boxCanvas(w: number, h: number, fill: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(w));
  canvas.height = Math.max(1, Math.ceil(h));
  const g = canvas.getContext("2d");
  if (!g) return canvas;
  g.fillStyle = fill;
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.strokeStyle = "#1a1422";
  g.lineWidth = 4;
  g.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
  return canvas;
}

/**
 * Coins, waking page elements, and the three exclusive weapons.
 * Solo play calls arm() for every page, including the archive.org demo, in Sandbox and Challenge.
 * Multiplayer leaves the fight asleep so rooms don't desync.
 */
export class PageFight {
  enabled = false;
  /**
   * Levels past the first in Journey. 0 matches Challenge hit points and
   * speed. Journey also raises the on-screen cap, shortens the spawn gap,
   * and lets contact deal damage. Challenge leaves `hazard` off.
   */
  pressure = 0;
  /** Contact damage, hearts, and the tighter Journey spawn curve. */
  hazard = false;
  /** Extra world pixels so the top boss bar clears the mobile HUD. Desktop stays at 0. */
  barLift = 0;
  purse = readCoins();
  charge = 0;
  stun = 0;
  private tagged = false;
  private loot = new Set<number>();
  private bossIndex = -1;
  private bossOut = false;
  /** Journey elite/boss that gates the portal. */
  private gateBody: Body | null = null;
  private gateClearedFlag = false;
  private gateBossFloor = false;
  private gateAffix: GateAffix = "tank";
  private broke = 0;
  private wake = 3.2;
  private t = 0;
  private vacuum = 0;
  private grace = 0;
  private lootWorth = LOOT_VALUE;
  private gemIndex = -1;
  private coinCombo = 0;
  private coinComboT = 0;
  private pendingCoinSfx = 0;
  /** Bitmask of CLEAR_BONUS steps already paid this page session. */
  private clearPaid = 0;
  /** Monsters that finished spawning on this page, and how many of those are dead. */
  private spawnedMonsters = 0;
  private slainMonsters = 0;
  private fullClearPaid = false;
  /** Coins already spilled from caches and ordinary destruction. Bonuses and boss pay sit outside this. */
  private pickupPaid = 0;
  private scrapX = 0;
  private scrapY = 0;
  private scrapT = 0;
  private coins: Coin[] = [];
  private hearts: Heart[] = [];
  private bodies: Body[] = [];
  private thrown: Body[] = [];
  private pull = { on: false, t: 0 };
  private magnetCd = 0;
  private magnetOut = false;
  private magnetLatch = false;
  private denyT = 0;
  private fieldT = 0;
  private edgeT = 0;
  private clackT = 0;
  private bits: MagnetBit[] = [];
  private tosses: MagnetToss[] = [];
  private shards: MagnetShard[] = [];
  private arcs: MagnetArc[] = [];
  private glyphAcc = 0;
  private cook = new Map<number, { t: number; cracks: number }>();
  private drillBooks = new WeakMap<Body, { token: number; dealt: number; next: number }>();
  private punch: Punchline;
  private stirs: Stir[] = [];
  private scars: Scar[] = [];
  private floaters: Floater[] = [];
  private bursts: Burst[] = [];
  private stamp: HTMLCanvasElement | null = null;
  private focus = { x: 0, y: 0 };

  constructor(private host: FightHost) {
    this.punch = new Punchline(host, (x0, y0, x1, y1) => this.punchBodies(x0, y0, x1, y1));
  }

  get holding(): boolean {
    return this.pull.on;
  }

  get lootCount(): number {
    return this.loot.size;
  }

  get minionCount(): number {
    return this.bodies.length + this.stirs.length;
  }

  get looseCoins(): number {
    return this.coins.length;
  }

  peace(seconds: number) {
    this.grace = Math.max(0, seconds);
    this.stun = 0;
  }

  wakeNow(): boolean {
    this.t = Math.max(this.t, 3);
    this.wake = 0;
    return this.wakeOne();
  }

  breakBoss(): boolean {
    if (this.bossIndex < 0) return false;
    const block = this.host.world.blocks[this.bossIndex];
    if (!block?.alive) return this.bossOut;
    this.host.world.damageBlock(this.bossIndex, block.hp + 40, 0.2, -0.6, block.x + block.w / 2, block.y + block.h / 2);
    return true;
  }

  probe() {
    const grabs = [];
    const world = this.host.world;
    for (let i = 0; i < world.blocks.length && grabs.length < 24; i++) {
      if (!this.magnetic(i)) continue;
      const block = world.blocks[i];
      grabs.push({ i, x: block.x, y: block.y, w: block.w, h: block.h });
    }
    return {
      x: this.host.x,
      y: this.host.y,
      coins: this.coins.map((coin) => ({ x: coin.x, y: coin.y, v: coin.value })),
      minions: this.bodies.map((body) => ({
        x: body.x, y: body.y, kind: body.kind, hp: Math.round(body.hp), eyes: body.eyes, w: body.w, h: body.h,
        phase: body.phase, max: Math.round(body.maxHp),
        affix: body.affix || "", gate: !!body.gate, phase2: !!body.phase2, title: body.title || "",
      })),
      waking: this.stirs.length,
      punch: this.punch.probe(),
      focus: { ...this.focus },
      held: this.bits.length > 0,
      charge: this.charge,
      stun: this.stun,
      bossIndex: this.bossIndex,
      bossOut: this.bossOut,
      bossMark: this.bossIndex >= 0 ? world.blocks[this.bossIndex]?.mark ?? "" : "",
      bossBox: this.bossIndex >= 0 && world.blocks[this.bossIndex]
        ? { x: world.blocks[this.bossIndex].x, y: world.blocks[this.bossIndex].y, w: world.blocks[this.bossIndex].w, h: world.blocks[this.bossIndex].h }
        : null,
      cd: { punch: this.punch.cooling },
      grabs,
      gate: this.gateInfo(),
    };
  }

  census(): { n: number; image: number; control: number; box: number; leaf: number; mid: number } {
    const tally = { n: 0, image: 0, control: 0, box: 0, leaf: 0, mid: 0 };
    for (const block of this.host.world.blocks) {
      tally.n++;
      if (block.kind === "image") tally.image++;
      else if (block.kind === "control") tally.control++;
      else tally.box++;
      if (!block.children.length) tally.leaf++;
      const area = block.w * block.h;
      if (block.alive && block.canDetach && area >= 700 && area <= 14000) tally.mid++;
    }
    return tally;
  }

  meter(id: string): number {
    if (id === "punchline") return this.punch.meter();
    if (id === "magnet") {
      if (this.pull.on) return Math.min(0.92, 0.12 + this.weightOf(this.bits) * 0.8);
      if (this.magnetCd > 0) return Math.min(0.98, 1 - this.magnetCd / MAGNET_CD);
      return 1;
    }
    return 1;
  }

  busy(): boolean {
    return this.pull.on || this.charge > 0.04 || this.punch.busy();
  }

  /** Pulling with the magnet blocks grenades. The glove does not. */
  grenadeBlocked(): boolean {
    return this.pull.on || this.charge > 0.04;
  }

  setHazard(on: boolean) {
    this.hazard = on;
  }

  setPressure(levelAbove: number) {
    const next = Math.max(0, Math.floor(levelAbove));
    this.pressure = next;
    if (!this.hazard) return;
    // Level 1 keeps the 3.2s first wake. Later levels open the first monster sooner.
    this.wake = Math.min(this.wake, Math.max(0.75, 3.2 - next * 0.32));
  }

  /** Journey: remember which elite/boss line this floor uses. */
  armGate(affix: GateAffix, bossFloor: boolean) {
    this.gateAffix = affix;
    this.gateBossFloor = bossFloor;
    this.gateBody = null;
    this.gateClearedFlag = false;
  }

  gateAlive(): boolean {
    return !!this.gateBody && this.gateBody.hp > 0 && !this.gateBody.countedDead;
  }

  gateCleared(): boolean {
    return this.gateClearedFlag;
  }

  gateInfo(): { alive: boolean; cleared: boolean; boss: boolean; affix: GateAffix; title: string } {
    return {
      alive: this.gateAlive(),
      cleared: this.gateClearedFlag,
      boss: this.gateBossFloor,
      affix: this.gateAffix,
      title: gateTitle(this.gateAffix, this.gateBossFloor),
    };
  }

  /**
   * Journey: at 60% wreck, peel a page element into the floor elite/boss.
   * Returns true when a new gate foe just appeared.
   */
  trySpawnGate(): boolean {
    if (!this.enabled || this.host.online || !this.hazard) return false;
    if (this.gateClearedFlag || this.gateAlive()) return false;
    if (this.gateBody) return false;
    return this.spawnGateFoe();
  }

  /** Debug: drop any living gate foe and re-arm for a new floor. */
  resetGate(affix: GateAffix, bossFloor: boolean) {
    if (this.gateBody && this.gateBody.hp > 0 && !this.gateBody.countedDead) {
      this.kill(this.gateBody);
    }
    this.gateBody = null;
    this.gateClearedFlag = false;
    this.armGate(affix, bossFloor);
  }

  /** Debug: apply raw damage to the living gate foe (for phase-two checks). */
  hurtGate(dmg: number): boolean {
    if (!this.gateBody || this.gateBody.hp <= 0 || this.gateBody.countedDead) return false;
    this.damageBody(this.gateBody, Math.max(0, dmg), 0, -1, false);
    return true;
  }

  /** Contact is 8–12 times a modest level scale. A boss slam stays near 20 on level 1. */
  private biteDamage(slam: boolean): number {
    const scale = 1 + Math.min(0.5, this.pressure * 0.08);
    if (slam) return Math.round(20 * scale);
    return Math.round((8 + Math.random() * 4) * scale);
  }

  private toughness(): number {
    return 1 + Math.min(0.6, this.pressure * 0.12);
  }

  private pace(): number {
    return 1 + Math.min(0.28, this.pressure * 0.07);
  }

  arm(url: string) {
    if (this.host.online || this.tagged) return;
    this.tagged = true;
    this.tag(url || "page");
  }

  noteBreak() {
    this.broke++;
    if (this.enabled && !this.host.online && this.broke === 1) this.wake = Math.min(this.wake, 0.9);
  }

  /** Coins, and whether this break should come back as the logo boss. */
  prepare(index: number, block: BreakBlock): boolean {
    const boss = this.enabled && !this.host.online && index === this.bossIndex && !this.bossOut;
    const cx = block.x + block.w / 2;
    const cy = block.y + block.h / 2;
    this.scrapX = cx;
    this.scrapY = cy;
    this.scrapT = 1.4;
    if (boss && !this.loot.has(index)) this.drop(cx, cy, BOSS_BREAK_COINS, false);
    else this.releaseLoot(index, cx, cy);
    return boss;
  }

  spawnBoss(slab: Slab | null, x: number, y: number, w: number, h: number) {
    if (this.bossOut || !this.enabled || this.host.online) return;
    this.bossOut = true;
    const body = slab ? this.fromSlab(slab, "boss", 8) : this.fallback(x, y, w, h, "boss", 8);
    const bossHp = BOSS_HP * this.toughness();
    body.hp = bossHp;
    body.maxHp = bossHp;
    body.eyes = true;
    body.slam = 1.4;
    body.title = "BOSS";
    body.phase = "telegraph";
    body.phaseT = 0;
    body.tele = 1;
    this.fit(body, 150);
    body.homeX = body.x;
    body.homeY = body.y;
    body.homeW = Math.max(body.w, w);
    body.homeH = Math.max(body.h, h);
    this.bodies.push(body);
    this.markBorn(body);
    this.scar(x - w / 2, y - h / 2, w, h);
    this.host.sfx("spawn");
    this.host.shake(0.16);
  }

  dropHeld() {
    this.pull.on = false;
    this.pull.t = 0;
    this.cook.clear();
    this.fieldT = 0;
    if (!this.bits.length) return;
    const vx = this.host.vx;
    const vy = Math.min(this.host.vy, -40);
    for (const bit of this.bits) this.settleBit(bit, vx, vy);
    this.bits.length = 0;
  }

  use(dt: number, fire: boolean, firePressed: boolean, kind: string) {
    if (kind === "magnet") {
      this.magnetLatch = true;
      if (this.magnetCd > 0) {
        if (firePressed) {
          this.denyT = 0.16;
          this.host.sfx("click", 0.7);
        }
        return;
      }
      if (fire) {
        if (!this.pull.on) {
          this.pull.on = true;
          this.pull.t = 0;
          this.fieldT = 0;
          this.host.sfx("click");
        }
      } else if (this.pull.on) this.releaseMagnet();
      return;
    }
    if (kind === "punch" && firePressed) this.punch.fire();
  }

  step(dt: number) {
    this.t += dt;
    this.stun = Math.max(0, this.stun - dt);
    this.vacuum = Math.max(0, this.vacuum - dt);
    this.grace = Math.max(0, this.grace - dt);
    this.magnetOut = this.magnetLatch;
    this.magnetLatch = false;
    this.magnetCd = Math.max(0, this.magnetCd - dt);
    this.denyT = Math.max(0, this.denyT - dt);
    this.fieldT = Math.max(0, this.fieldT - dt);
    this.clackT = Math.max(0, this.clackT - dt);
    for (let i = this.arcs.length - 1; i >= 0; i--) {
      this.arcs[i].life -= dt;
      if (this.arcs[i].life <= 0) this.arcs.splice(i, 1);
    }
    this.stepJuice(dt);
    this.scrapT = Math.max(0, this.scrapT - dt);
    this.spillScrap();
    this.stepMagnet(dt);
    this.stepCoins(dt);
    this.stepHearts(dt);
    this.punch.step(dt);
    this.stepThrown(dt);
    if (!this.enabled || this.host.online) {
      this.stirs.length = 0;
      this.flop();
      return;
    }
    const target = this.aggro();
    this.focus.x = target.x;
    this.focus.y = target.y;
    this.stepStirs(dt);
    this.stepMinions(dt);
    this.wake = Math.max(0, this.wake - dt);
    const cap = this.hazard
      ? Math.min(9, 3 + this.pressure)
      : 4 + Math.min(2, Math.floor(this.broke / 6));
    const ready = this.hazard ? this.t > Math.max(0.8, 2.2 - this.pressure * 0.18) : this.t > 2.4;
    if (this.grace <= 0 && this.wake <= 0 && this.bodies.length + this.stirs.length < cap && ready) {
      if (this.hazard) {
        const floor = Math.max(0.8, 2.35 - this.pressure * 0.2);
        this.wake = Math.max(floor, 3.15 - this.broke * 0.07 - this.t * 0.02 - this.pressure * 0.24);
      } else {
        this.wake = Math.max(2.5, 5.6 - this.broke * 0.11 - this.t * 0.025);
      }
      this.wakeOne();
    }
  }

  /** True when this block is still a seeded loot cache. Check before damageBlock. */
  isCache(index: number): boolean {
    return this.loot.has(index);
  }

  /**
   * One budgeted bonus coin from a drill. Returns whether a coin actually spawned.
   * No-op in multiplayer and once the page's pickup budget is spent.
   */
  drillSpoil(x: number, y: number): boolean {
    if (this.host.online || !this.tagged) return false;
    const paid = this.pickupPaid;
    this.drop(x, y, 1, false, 0, true);
    return this.pickupPaid > paid;
  }

  /**
   * Contact from a drill bit. 60 per hit, then a gap, capped per body for this shot.
   * A basic minion has 52 HP, so the first touch kills it. The bit does not stop.
   * The boss uses the same cap, so one bore cannot delete it.
   */
  drillBite(x: number, y: number, r: number, dmg: number, cap: number, token: number, now: number, every = 0.16) {
    for (const body of this.bodies) {
      if (body.hp <= 0) continue;
      if (this.boxDist(body, x, y) > r) continue;
      let book = this.drillBooks.get(body);
      if (!book || book.token !== token) {
        book = { token, dealt: 0, next: 0 };
        this.drillBooks.set(body, book);
      }
      if (now < book.next || book.dealt >= cap) continue;
      const deal = Math.min(dmg, cap - book.dealt);
      if (deal <= 0) continue;
      book.dealt += deal;
      book.next = now + every;
      this.damageBody(body, deal, body.x - x, body.y - y, false);
    }
  }

  /**
   * Splash damage. `direct` is a body this projectile actually struck.
   * That body takes the full amount, so a contact rocket deals 88 and kills a normal minion.
   */
  hurt(x: number, y: number, r: number, dmg: number, direct = -1) {
    const struck = direct >= 0 && direct < this.bodies.length ? this.bodies[direct] : null;
    this.peelInRadius(x, y, r);
    for (const body of [...this.bodies]) {
      const forced = body === struck;
      const edge = this.boxDist(body, x, y);
      if (!forced && edge > r) continue;
      const contact = forced || edge <= 16;
      const fall = contact || r <= 14 ? 1 : Math.max(0.45, 1 - edge / Math.max(1, r));
      this.damageBody(body, dmg * fall, body.x - x, body.y - y, forced && dmg >= 70);
    }
  }

  /**
   * Nearest living body in an aim cone, using the drawn sprite.
   * Sandbox and multiplayer have no bodies, so this stays empty.
   */
  aimPoint(x: number, y: number, ang: number, cone: number, reach: number): { x: number; y: number; dist: number } | null {
    let best: { x: number; y: number; dist: number } | null = null;
    const x1 = x + Math.cos(ang) * reach;
    const y1 = y + Math.sin(ang) * reach;
    for (const body of this.bodies) {
      if (body.hp <= 0) continue;
      const [l, t, r, b] = this.hitBox(body);
      const cx = (l + r) / 2;
      const cy = (t + b) / 2;
      const dist = Math.hypot(cx - x, cy - y);
      if (dist < 12 || dist > reach) continue;
      let aimed = segBox(x, y, x1, y1, l, t, r, b) >= 0;
      if (!aimed) {
        const corners: [number, number][] = [[l, t], [r, t], [l, b], [r, b], [cx, cy]];
        for (const [px, py] of corners) {
          let off = Math.atan2(py - y, px - x) - ang;
          off = Math.atan2(Math.sin(off), Math.cos(off));
          if (Math.abs(off) <= cone) {
            aimed = true;
            break;
          }
        }
      }
      if (!aimed) continue;
      if (!best || dist < best.dist) best = { x: cx, y: cy, dist };
    }
    return best;
  }

  /** Fraction along the segment of a minion or a waking element. No damage and no peel. */
  touch(x0: number, y0: number, x1: number, y1: number): number {
    let best = -1;
    for (const stir of this.stirs) {
      const t = segBox(x0, y0, x1, y1, stir.x, stir.y, stir.x + stir.w, stir.y + stir.h);
      if (t >= 0 && (best < 0 || t < best)) best = t;
    }
    for (const body of this.bodies) {
      const t = this.segBody(x0, y0, x1, y1, body);
      if (t >= 0 && (best < 0 || t < best)) best = t;
    }
    return best;
  }

  obstruct(x0: number, y0: number, x1: number, y1: number): number {
    return this.intercept(x0, y0, x1, y1).t;
  }

  /** Closest fight body on the segment, after peeling a waking element in front of it. `pad` inflates the box. */
  intercept(x0: number, y0: number, x1: number, y1: number, pad = 0): { t: number; index: number } {
    let hit = this.pickBody(x0, y0, x1, y1, pad);
    const stirred = this.peelFirstStir(x0, y0, x1, y1, hit.t < 0 ? 1 : hit.t);
    if (stirred >= 0) {
      const again = this.pickBody(x0, y0, x1, y1, pad);
      if (again.index >= 0 && (hit.index < 0 || again.t <= hit.t)) hit = again;
      else if (hit.index < 0 && this.bodies.length) hit = { t: stirred, index: this.bodies.length - 1 };
    }
    return hit;
  }

  /** Nearest living body within `reach` of a point, using the drawn sprite. -1 if none. */
  nearestBody(x: number, y: number, reach: number): number {
    let best = -1;
    let bestD = reach;
    for (let i = 0; i < this.bodies.length; i++) {
      const body = this.bodies[i];
      if (body.hp <= 0) continue;
      const dist = this.nearDist(body, x, y);
      if (dist <= bestD) {
        bestD = dist;
        best = i;
      }
    }
    return best;
  }

  /** Returns the hit fraction along the segment when a minion is in front of it. */
  clipSeg(x0: number, y0: number, x1: number, y1: number, dmg: number, dirX = 0, dirY = 0, budget?: { token: number; limit: number }): number {
    const hit = this.intercept(x0, y0, x1, y1);
    if (hit.t < 0 || hit.index < 0) return -1;
    const who = this.bodies[hit.index];
    if (who) {
      const deal = budget ? this.limitShot(budget.token, who, dmg, budget.limit) : dmg;
      if (deal > 0) {
        const dx = dirX || x1 - x0;
        const dy = dirY || y1 - y0;
        this.damageBody(who, deal, dx, dy, deal >= RAIL_DMG);
      }
    }
    return hit.t;
  }

  private shotToken = 0;
  private shotUsed = new Map<Body, number>();

  /** Cap how much one shotgun trigger can deal to one body. */
  private limitShot(token: number, body: Body, dmg: number, limit: number): number {
    if (token !== this.shotToken) {
      this.shotToken = token;
      this.shotUsed = new Map();
    }
    const used = this.shotUsed.get(body) ?? 0;
    const deal = Math.max(0, Math.min(dmg, limit - used));
    if (deal > 0) this.shotUsed.set(body, used + deal);
    return deal;
  }

  breakFirst(): boolean {
    for (const id of this.loot) {
      const block = this.host.world.blocks[id];
      if (!block?.alive) continue;
      this.host.world.damageBlock(id, block.hp + 8, 0.2, -0.7, block.x + block.w / 2, block.y + block.h / 2);
      return true;
    }
    return false;
  }

  draw(ctx: CanvasRenderingContext2D, view?: { x: number; y: number; w: number; h: number }) {
    heroFx.grab = this.pull.on ? 1 : this.denyT > 0 ? 0.45 : 0;
    heroFx.charge = this.charge;
    heroFx.magnet = this.magnetOut ? 1 : 0;
    const world = this.host.world;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.lineWidth = 2;
    for (const scar of this.scars) this.drawScar(ctx, scar);
    for (const stir of this.stirs) this.drawStir(ctx, stir);
    this.drawCook(ctx);
    for (const id of this.loot) this.drawLootGlint(ctx, world, id, view);
    for (const coin of this.coins) this.drawCoin(ctx, coin, view);
    for (const heart of this.hearts) this.drawHeart(ctx, heart, view);
    if (this.pull.on || this.fieldT > 0) this.drawCone(ctx, this.pull.on ? 0.1 : 0.1 * (this.fieldT / 0.22));
    else if (this.denyT > 0) this.drawDeny(ctx);
    this.punch.drawDebris(ctx);
    for (const body of this.bodies) this.drawBody(ctx, body);
    for (const body of this.thrown) this.drawBody(ctx, body);
    if (this.bits.length) this.drawClumpAura(ctx, this.bits, this.weightOf(this.bits));
    for (const bit of this.bits) this.drawBit(ctx, bit);
    for (const toss of this.tosses) {
      this.drawClumpAura(ctx, toss.bits, toss.weight);
      for (const bit of toss.bits) this.drawBit(ctx, bit);
    }
    this.drawArcs(ctx);
    for (const shard of this.shards) this.drawBit(ctx, shard.bit);
    for (const burst of this.bursts) this.drawBurst(ctx, burst);
    for (const num of this.floaters) this.drawFloater(ctx, num);
    const gate = this.bodies.find((body) => body.gate && body.hp > 0);
    const boss = gate || this.bodies.find((body) => body.kind === "boss" && body.hp > 0);
    if (boss) this.drawBossBar(ctx, boss, view);
    ctx.restore();
  }

  /** Spring, glove, starburst, and dust. Drawn after the stick so the glove stays in front. */
  drawFront(ctx: CanvasRenderingContext2D) {
    this.punch.drawFront(ctx);
  }

  private tag(url: string) {
    const world = this.host.world;
    const seed = hashUrl(url);
    const images = world.blocks
      .map((block, index) => ({ block, index }))
      .filter(({ block }) => block.kind === "image" && block.canDetach && block.w * block.h >= 800 && block.w * block.h <= 90000);
    const logos = world.blocks
      .map((block, index) => ({ block, index }))
      .filter(({ block }) => block.mark === "logo" && block.kind === "image" && block.canDetach && block.y < 520 && block.w * block.h >= 360 && block.w * block.h <= 120000);
    if (logos.length) {
      logos.sort((a, b) => b.block.w * b.block.h - a.block.w * a.block.h);
      this.bossIndex = logos[0].index;
    } else {
      const header = images.filter(({ block }) => block.y < 180);
      const logoPool = header.length ? header : images;
      if (logoPool.length) {
        logoPool.sort((a, b) => b.block.w * b.block.h - a.block.w * a.block.h);
        this.bossIndex = logoPool[0].index;
      }
    }
    const zone = this.payZone();
    const take = (relax: boolean) => world.blocks
      .map((block, index) => ({ block, index }))
      .filter(({ block }) => {
        if (!block.alive || !block.canDetach) return false;
        const area = block.w * block.h;
        if (block.w < 10 || block.h < 10) return false;
        if (area < (relax ? 200 : 360) || area > (relax ? 220000 : 140000)) return false;
        const overlap = Math.min(block.y + block.h, zone) - Math.max(block.y, 0);
        return overlap >= Math.min(block.h, relax ? 8 : 12);
      });
    let pool = take(false);
    if (pool.length < 8) pool = take(true);
    pool = pool.filter((item) => item.index !== this.bossIndex);
    const bands = 5;
    const buckets: Array<typeof pool> = Array.from({ length: bands }, () => []);
    for (const item of pool) {
      const y = Math.max(0, Math.min(zone - 1, item.block.y));
      const band = Math.min(bands - 1, Math.floor((y / zone) * bands));
      buckets[band].push(item);
    }
    for (const bucket of buckets) {
      bucket.sort((a, b) => {
        const boxA = a.block.kind === "box" ? 1 : 0;
        const boxB = b.block.kind === "box" ? 1 : 0;
        if (boxA !== boxB) return boxA - boxB;
        return mix(seed, a.index) - mix(seed, b.index);
      });
    }
    const want = Math.min(pool.length, LOOT_MIN + (seed % LOOT_EXTRA));
    let guard = 0;
    while (this.loot.size < want && guard < pool.length * bands + bands) {
      const item = buckets[guard % bands].shift();
      guard++;
      if (item) this.loot.add(item.index);
    }
    if (this.loot.size < want) {
      const rest = pool.filter((item) => !this.loot.has(item.index));
      rest.sort((a, b) => mix(seed, a.index) - mix(seed, b.index));
      for (const item of rest) {
        if (this.loot.size >= want) break;
        this.loot.add(item.index);
      }
    }
    if (this.bossIndex < 0) {
      let best = -1;
      let bestArea = 0;
      for (let i = 0; i < world.blocks.length; i++) {
        const block = world.blocks[i];
        if (!block.canDetach || block.y > 220) continue;
        const area = block.w * block.h;
        if (area < 900 || area > 24000 || area <= bestArea) continue;
        bestArea = area;
        best = i;
      }
      this.bossIndex = best;
    }
    if (this.bossIndex >= 0) this.loot.add(this.bossIndex);
    const regular = [...this.loot].filter((id) => id !== this.bossIndex);
    this.lootWorth = regular.length >= LOOT_MIN
      ? LOOT_VALUE
      : Math.max(LOOT_VALUE, Math.min(6, Math.ceil(48 / Math.max(1, regular.length))));
    let gem = -1;
    let gemScore = -1;
    for (const id of regular) {
      const score = mix(seed ^ 0x51ed, id);
      if (score > gemScore) {
        gemScore = score;
        gem = id;
      }
    }
    this.gemIndex = gem;
  }

  /** Spill a cache. The boss block pays its break purse instead of the normal stack. */
  private releaseLoot(index: number, x: number, y: number) {
    if (!this.loot.has(index)) return;
    const bossBlock = index === this.bossIndex;
    const gem = index === this.gemIndex;
    this.loot.delete(index);
    if (bossBlock && this.enabled && !this.host.online) this.drop(x, y, BOSS_BREAK_COINS, false);
    else {
      this.drop(x, y, this.lootWorth, gem, LOOT_GEM, true);
      this.maybeHeart(x, y, 0.12);
    }
  }

  private drop(x: number, y: number, count: number, gem: boolean, gemValue = LOOT_GEM, budgeted = false) {
    let n = Math.max(1, Math.min(12, count));
    let gemPay = gem ? gemValue : 0;
    if (budgeted) {
      const room = PICKUP_BUDGET - this.pickupPaid;
      if (room <= 0) return;
      n = Math.min(n, room);
      gemPay = Math.min(gemPay, Math.max(0, room - n));
    }
    for (let i = 0; i < n; i++) this.spawnCoin(x, y, 1, false);
    if (gemPay > 0) this.spawnCoin(x, y, gemPay, true);
    if (budgeted) this.pickupPaid += n + gemPay;
    if (this.coins.length > 240) this.coins.splice(0, this.coins.length - 240);
  }

  private maybeHeart(x: number, y: number, chance: number) {
    if (!this.hazard || chance <= 0 || Math.random() > chance) return;
    const ang = Math.random() * Math.PI * 2;
    const speed = 70 + Math.random() * 110;
    this.hearts.push({
      x, y,
      vx: Math.cos(ang) * speed,
      vy: -150 - Math.random() * 120,
      life: 16,
      spin: Math.random() * 6,
    });
    if (this.hearts.length > 8) this.hearts.splice(0, this.hearts.length - 8);
  }

  private stepHearts(dt: number) {
    if (!this.hazard) {
      this.hearts.length = 0;
      return;
    }
    if (!this.hearts.length) return;
    const px = this.host.x;
    const py = this.host.y - 22;
    const world = this.host.world;
    for (let i = this.hearts.length - 1; i >= 0; i--) {
      const heart = this.hearts[i];
      heart.life -= dt;
      heart.vy = Math.min(640, heart.vy + 1200 * dt);
      heart.x += heart.vx * dt;
      heart.y += heart.vy * dt;
      if (world.matAt(heart.x, heart.y + 6) >= SOLID && heart.vy > 0) {
        heart.vy *= -0.42;
        heart.vx *= 0.7;
        heart.y -= 2;
      }
      heart.x = Math.max(8, Math.min(world.W - 8, heart.x));
      if (heart.y > world.H - 8) {
        heart.y = world.H - 8;
        heart.vy = -Math.abs(heart.vy) * 0.35;
      }
      const dx = px - heart.x;
      const dy = py - heart.y;
      if (dx * dx + dy * dy < 36 * 36 && this.host.heal?.(15)) {
        this.spawnNum(heart.x, heart.y - 18, 15, true, "#ff8aa0", undefined, true);
        this.hearts.splice(i, 1);
        continue;
      }
      if (heart.life <= 0) this.hearts.splice(i, 1);
    }
  }

  private drawHeart(ctx: CanvasRenderingContext2D, heart: Heart, view?: { x: number; y: number; w: number; h: number }) {
    if (view) {
      if (heart.x < view.x - 24 || heart.x > view.x + view.w + 24) return;
      if (heart.y < view.y - 24 || heart.y > view.y + view.h + 24) return;
    }
    if (heart.life < 4 && Math.sin(heart.life * 14) < 0) return;
    const rows = [
      ".rr.rr.",
      "rrwwwrr",
      "rrrrrrr",
      ".rrrrr.",
      "..rrr..",
      "...r...",
    ];
    const s = 3;
    const bob = Math.sin(this.t * 5 + heart.spin);
    const left = Math.round(heart.x - (rows[0].length * s) / 2);
    const top = Math.round(heart.y - rows.length * s + bob);
    const stamp = (ox: number, oy: number, color: string) => {
      ctx.fillStyle = color;
      for (let gy = 0; gy < rows.length; gy++) {
        for (let gx = 0; gx < rows[gy].length; gx++) {
          if (rows[gy][gx] === ".") continue;
          ctx.fillRect(left + gx * s + ox, top + gy * s + oy, s, s);
        }
      }
    };
    for (const [ox, oy] of [[-2, 0], [2, 0], [0, -2], [0, 2], [-2, -2], [2, -2], [-2, 2], [2, 2]] as const) stamp(ox, oy, "#140818");
    for (let gy = 0; gy < rows.length; gy++) {
      for (let gx = 0; gx < rows[gy].length; gx++) {
        const cell = rows[gy][gx];
        if (cell === ".") continue;
        ctx.fillStyle = cell === "w" ? "#ffd5de" : "#ff3d62";
        ctx.fillRect(left + gx * s, top + gy * s, s, s);
      }
    }
    ctx.fillStyle = "rgba(26, 20, 34, 0.45)";
    ctx.fillRect(left + 2, top + rows.length * s + 1, rows[0].length * s - 4, 2);
  }

  private spawnCoin(x: number, y: number, value: number, gem: boolean) {
    const ang = Math.random() * Math.PI * 2;
    const speed = 90 + Math.random() * 170;
    this.coins.push({
      x, y,
      vx: Math.cos(ang) * speed,
      vy: -180 - Math.random() * 200,
      gem, value, life: COIN_LIFE,
      spin: Math.random() * 6,
    });
  }

  private gain(n: number) {
    if (n <= 0) return;
    this.purse += n;
    writeCoins(this.purse);
  }

  /**
   * Pay each destruction milestone at most once this page session.
   * Solo Sandbox and Challenge only — the same gate as loot.
   * Returns the coins just granted, or 0.
   */
  awardClear(fraction: number): number {
    if (this.host.online || !this.tagged) return 0;
    let sum = 0;
    for (let i = 0; i < CLEAR_BONUS.length; i++) {
      if (this.clearPaid & (1 << i)) continue;
      const step = CLEAR_BONUS[i];
      const gate = i === CLEAR_BONUS.length - 1 ? 0.999 : step.at;
      if (fraction < gate) continue;
      this.clearPaid |= 1 << i;
      sum += step.coins;
    }
    if (sum <= 0) return 0;
    this.gain(sum);
    this.host.sfx("coin", 0.92);
    return sum;
  }

  monsterTally(): { spawned: number; slain: number; living: number } {
    return { spawned: this.spawnedMonsters, slain: this.slainMonsters, living: this.livingMonsters() };
  }

  /** Every monster that finished spawning on this page is dead. Waking pieces do not count yet. */
  monstersCleared(): boolean {
    return this.spawnedMonsters > 0 && this.slainMonsters >= this.spawnedMonsters && this.livingMonsters() === 0;
  }

  /**
   * Journey full-clear bonus. Monsters still up do not block the portal.
   * Returns the coins just granted, or 0.
   */
  awardFullClear(): number {
    // Cancel peels that have not birthed yet so a late crack does not spoil the bonus.
    this.stirs.length = 0;
    if (this.fullClearPaid || !this.monstersCleared()) return 0;
    this.fullClearPaid = true;
    this.gain(PORTAL_FULL_CLEAR_COINS);
    this.host.sfx("coin", 0.92);
    return PORTAL_FULL_CLEAR_COINS;
  }

  /** Debug / QA: kill every living monster, including thrown scraps, and cancel peels. */
  slayAll(): number {
    this.stirs.length = 0;
    let n = 0;
    const living = this.bodies.filter((body) => body.hp > 0 && !body.countedDead);
    for (const body of living) {
      this.kill(body);
      n++;
    }
    for (const body of [...this.thrown]) {
      if (body.hp > 0 && !body.countedDead) {
        this.noteDead(body);
        n++;
      }
    }
    this.thrown.length = 0;
    return n;
  }

  private collect(coin: Coin) {
    this.gain(coin.value);
    if (coin.gem) this.vacuum = 2.6;
    this.coinCombo = Math.min(7, this.coinCombo + 1);
    this.coinComboT = 0.72;
    this.pendingCoinSfx = this.coinCombo;
    this.spawnNum(
      coin.x, coin.y - 14, coin.value, true,
      coin.gem ? "#e4d4ff" : "#ffd84d",
      coin.gem ? "gem" : "coin",
    );
    this.host.onPickup?.(coin.value, coin.x, coin.y, coin.gem);
  }

  /** Ease a coin toward the player. Gravity is skipped so the pull can lift it. */
  private pullCoin(coin: Coin, px: number, py: number, dist: number, dt: number, strong: boolean) {
    const reach = strong ? COIN_VACUUM : COIN_MAGNET;
    const n = Math.max(0, Math.min(1, 1 - dist / reach));
    const ease = n * n * (3 - 2 * n);
    const ux = (px - coin.x) / dist;
    const uy = (py - coin.y) / dist;
    let radial = coin.vx * ux + coin.vy * uy;
    let tx = coin.vx - radial * ux;
    let ty = coin.vy - radial * uy;
    if (radial < 0) radial *= Math.exp(-(strong ? 28 : 22) * dt);
    const damp = Math.exp(-(strong ? 8 : 6) * dt);
    tx *= damp;
    ty *= damp;
    const speed = (strong ? 220 : 130) + ease * (strong ? 880 : 700);
    const rate = (strong ? 7 : 4.5) + ease * (strong ? 12 : 11);
    radial += (speed - radial) * (1 - Math.exp(-rate * dt));
    coin.vx = tx + ux * radial;
    coin.vy = ty + uy * radial;
    coin.x += coin.vx * dt;
    coin.y += coin.vy * dt;
    const dx = px - coin.x;
    const dy = py - coin.y;
    const next = Math.hypot(dx, dy) || 1;
    if (next <= reach) return;
    coin.x = px - (dx / next) * (reach - 2);
    coin.y = py - (dy / next) * (reach - 2);
    const urx = (px - coin.x) / reach;
    const ury = (py - coin.y) / reach;
    const out = coin.vx * -urx + coin.vy * -ury;
    if (out > 0) {
      coin.vx += urx * out;
      coin.vy += ury * out;
    }
  }

  /** Magnet grab: the body box, or close enough that a top-of-page coin cannot hover. */
  private heldCoin(coin: Coin, px: number, py: number): boolean {
    const dx = coin.x - px;
    const dy = coin.y - py;
    if (dx * dx + dy * dy <= COIN_GRAB * COIN_GRAB) return true;
    const x = this.host.x;
    const feet = this.host.y;
    return coin.x >= x - 18 && coin.x <= x + 18 && coin.y >= feet - 66 && coin.y <= feet + 8;
  }

  /**
   * How far down the page you have to shoot before both destruction caps fill.
   * Loot bands use this span instead of the full document height.
   */
  private payZone(): number {
    const world = this.host.world;
    const letters = this.host.letters;
    const n = letters.x.length;
    const letterCap = Math.min(0.6 * Math.max(1, n), 2400);
    const cellCap = Math.min(0.3 * world.totalCells, 120000);
    const bucketH = 32;
    const rows = Math.max(1, Math.ceil(world.H / bucketH));
    const counts = new Uint32Array(rows);
    for (let i = 0; i < n; i++) {
      const row = Math.min(rows - 1, Math.max(0, Math.floor(letters.y[i] / bucketH)));
      counts[row]++;
    }
    let yLetter = n > 0 ? world.H : 0;
    let seenL = 0;
    for (let row = 0; row < rows; row++) {
      seenL += counts[row];
      if (seenL >= letterCap) {
        yLetter = (row + 1) * bucketH;
        break;
      }
    }
    let yCell = world.H;
    let seenC = 0;
    const gw = world.GW;
    const mat = world.mat;
    for (let row = 0; row < world.GH && seenC < cellCap; row++) {
      const off = row * gw;
      for (let col = 0; col < gw; col++) if (mat[off + col] >= SOLID) seenC++;
      if (seenC >= cellCap) yCell = (row + 1) * CELL;
    }
    return Math.max(220, Math.min(world.H, Math.max(yLetter, yCell) + 64));
  }

  /** Top the purse up to the destruction curve. Caches already counted in pickupPaid. */
  private spillScrap() {
    if (this.host.online || !this.tagged) return;
    const raw = this.host.destruction();
    const frac = raw >= 0.999 ? 1 : Math.max(0, Math.min(1, raw));
    const due = Math.round(PICKUP_BUDGET * frac) - this.pickupPaid;
    if (due <= 0) return;
    const n = Math.min(due, 3);
    const px = this.host.x;
    const py = this.host.y - 22;
    const nearBreak = this.scrapT > 0 && Math.hypot(this.scrapX - px, this.scrapY - py) < COIN_MAGNET;
    for (let i = 0; i < n; i++) {
      const ang = this.host.aim + (i - (n - 1) / 2) * 0.22;
      const dist = 28 + i * 8;
      const sx = nearBreak ? this.scrapX : px + Math.cos(ang) * dist;
      const sy = nearBreak ? this.scrapY : py + Math.sin(ang) * dist;
      this.spawnCoin(sx + (Math.random() - 0.5) * 10, sy + (Math.random() - 0.5) * 8, 1, false);
    }
    this.pickupPaid += n;
    if (this.coins.length > 240) this.coins.splice(0, this.coins.length - 240);
  }

  private stepCoins(dt: number) {
    this.coinComboT = Math.max(0, this.coinComboT - dt);
    if (this.coinComboT <= 0) this.coinCombo = 0;
    const strong = this.vacuum > 0 || this.magnetOut;
    const reach = strong ? COIN_VACUUM : COIN_MAGNET;
    const px = this.host.x;
    const py = this.host.y - 22;
    const world = this.host.world;
    for (let i = this.coins.length - 1; i >= 0; i--) {
      const coin = this.coins[i];
      coin.life -= dt;
      const dx = px - coin.x;
      const dy = py - coin.y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist < reach) {
        // No page clamp here. A coin pulled toward a player at the top must be
        // allowed above y=8, or it hovers just outside the old 24px pickup.
        if (!this.heldCoin(coin, px, py)) {
          this.pullCoin(coin, px, py, dist, dt, strong);
          const nd = Math.hypot(px - coin.x, py - coin.y) || 1;
          if (nd > COIN_GRAB && nd >= dist - 0.25) {
            const step = Math.min(nd - (COIN_GRAB - 1), Math.max(24 * dt, 0.8));
            coin.x += ((px - coin.x) / nd) * step;
            coin.y += ((py - coin.y) / nd) * step;
          }
        }
        if (this.heldCoin(coin, px, py)) {
          this.collect(coin);
          this.coins.splice(i, 1);
          continue;
        }
      } else {
        coin.vy = Math.min(720, coin.vy + 1400 * dt);
        coin.x += coin.vx * dt;
        coin.y += coin.vy * dt;
        if (world.matAt(coin.x, coin.y + 6) >= SOLID && coin.vy > 0) {
          coin.vy *= -0.45;
          coin.vx *= 0.72;
          coin.y -= 3;
        }
        if (coin.x < 8) {
          coin.x = 8;
          coin.vx = Math.abs(coin.vx) * 0.65;
        } else if (coin.x > world.W - 8) {
          coin.x = world.W - 8;
          coin.vx = -Math.abs(coin.vx) * 0.65;
        }
        if (coin.y < 8) {
          coin.y = 8;
          coin.vy = Math.abs(coin.vy) * 0.4;
        } else if (coin.y > world.H - 8) {
          coin.y = world.H - 8;
          coin.vy = -Math.abs(coin.vy) * 0.38;
          coin.vx *= 0.8;
        }
      }
      if (coin.life <= 0) this.coins.splice(i, 1);
    }
    if (this.pendingCoinSfx > 0) {
      this.host.sfx("coin", 1 + (this.pendingCoinSfx - 1) * 0.085);
      this.pendingCoinSfx = 0;
    }
  }

  private aggro(): { x: number; y: number } {
    return { x: this.host.x, y: this.host.y - 24 };
  }

  /** Glove passes through fighters. Minions fly; the boss is shoved and stays. */
  private punchBodies(x0: number, y0: number, x1: number, y1: number) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    for (const body of this.bodies.slice()) {
      if (body.hit > 0 || body.hp <= 0) continue;
      const along = (body.x - x0) * ux + (body.y - y0) * uy;
      const t = Math.max(0, Math.min(len, along));
      const cx = x0 + ux * t;
      const cy = y0 + uy * t;
      const reach = 18 + Math.min(body.w, body.h) * 0.35;
      if (Math.hypot(body.x - cx, body.y - cy) > reach) continue;
      this.launchBody(body, ux, uy);
    }
  }

  private launchBody(body: Body, ux: number, uy: number) {
    const boss = body.kind === "boss";
    body.hp -= PUNCH.minionDmg;
    body.flash = 0.16;
    body.bar = 1.7;
    body.hit = 0.35;
    this.hitSparks(body);
    this.spawnNum(body.x, body.y - Math.max(12, body.h * 0.46), PUNCH.minionDmg, false, "#fff6d8", undefined, true);
    this.host.sfx("hitMinion");
    if (boss) {
      if (body.hp <= 0) {
        this.kill(body);
        return;
      }
      body.shoveT = 0.28;
      body.vx = ux * PUNCH.bossKnock;
      body.vy = uy * PUNCH.bossKnock - 60;
      return;
    }
    const at = this.bodies.indexOf(body);
    if (at >= 0) this.bodies.splice(at, 1);
    body.kind = "thrown";
    body.eyes = false;
    body.launched = true;
    body.dug = false;
    body.vx = ux * PUNCH.minionKnock;
    body.vy = uy * PUNCH.minionKnock - 40;
    body.av = (Math.random() - 0.5) * 8;
    body.age = 0;
    body.hit = 0;
    this.thrown.push(body);
  }

  private magnetic(index: number): boolean {
    const block = this.host.world.blocks[index];
    if (!block?.alive || !block.canDetach || block.children.length > 0) return false;
    if (index === this.bossIndex || block.mark === "logo") return false;
    const area = block.w * block.h;
    return area >= 80 && area <= 42000 && block.w >= 6 && block.h >= 6 && block.w <= 340 && block.h <= 260;
  }

  private magnetOrigin(): { x: number; y: number } {
    const aim = this.host.aim;
    const face = this.host.facing || 1;
    const acrossX = -Math.sin(aim) * face;
    const acrossY = Math.cos(aim) * face;
    return {
      x: this.host.x + Math.cos(aim) * 22 + acrossX * 4,
      y: this.host.y - 36 + Math.sin(aim) * 22 + acrossY * 4,
    };
  }

  private inCone(x: number, y: number): boolean {
    const origin = this.magnetOrigin();
    const dx = x - origin.x;
    const dy = y - origin.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= 12) return true;
    if (dist > MAGNET_RANGE) return false;
    const off = Math.atan2(Math.sin(Math.atan2(dy, dx) - this.host.aim), Math.cos(Math.atan2(dy, dx) - this.host.aim));
    return Math.abs(off) <= MAGNET_HALF;
  }

  private weightOf(bits: MagnetBit[]): number {
    let mass = 0;
    for (const bit of bits) mass += bit.mass;
    return Math.max(0, Math.min(1, mass / MAGNET_MASS_FULL));
  }

  private roomFor(area: number): boolean {
    if (this.bits.length >= MAGNET_CAP) return false;
    let sum = 0;
    for (const bit of this.bits) sum += bit.area;
    return sum + area <= MAGNET_AREA_CAP;
  }

  private packSlot(i: number, count: number): { x: number; y: number } {
    const grow = 0.7 + Math.sqrt(Math.max(1, count)) * 0.28;
    const ang = i * 2.399963;
    const ring = (10 + Math.sqrt(i) * 8) * grow;
    return { x: Math.cos(ang) * ring * 0.72, y: Math.sin(ang) * ring * 0.72 };
  }

  private clack() {
    if (this.clackT > 0) return;
    this.clackT = 0.07;
    this.host.sfx("hitChunk");
  }

  private releaseMagnet() {
    const heldFor = this.pull.t;
    this.pull.on = false;
    this.pull.t = 0;
    this.glyphAcc = 0;
    this.cook.clear();
    const quick = heldFor > 0 && heldFor <= MAGNET_TAP_HOLD;
    if (quick && this.weightOf(this.bits) < MAGNET_TAP_WEIGHT) {
      this.tapPulse();
      return;
    }
    if (this.bits.length) {
      this.throwClump(false);
      return;
    }
    this.fieldT = 0.22;
  }

  private stepMagnet(dt: number) {
    if (this.pull.on && !this.magnetOut) this.dropHeld();
    if (this.pull.on) {
      this.pull.t += dt;
      this.suck(dt);
      this.edgeT += dt;
      if (this.edgeT >= 0.06) {
        this.edgeT = 0;
        this.sprayCone();
      }
      if (this.pull.t >= MAGNET_HOLD_MAX && this.bits.length) this.releaseMagnet();
    } else if (this.cook.size) this.cook.clear();
    this.stepTosses(dt);
    this.stepShards(dt);
  }

  private sprayCone() {
    const origin = this.magnetOrigin();
    const aim = this.host.aim;
    for (const side of [-1, 1]) {
      const ang = aim + MAGNET_HALF * side;
      const dist = 36 + Math.random() * (MAGNET_RANGE - 48);
      this.host.parts.chip(
        origin.x + Math.cos(ang) * dist,
        origin.y + Math.sin(ang) * dist,
        Math.cos(aim) * 70,
        Math.sin(aim) * 70,
        hex("#ffd0d0"),
        0.22,
      );
    }
  }

  private suck(dt: number) {
    const world = this.host.world;
    const origin = this.magnetOrigin();
    const reach = MAGNET_RANGE + 40;
    const seen = new Set<number>();
    let rips = 0;
    let cracked = false;
    let ripped = false;
    for (let i = 0; i < world.blocks.length; i++) {
      const block = world.blocks[i];
      if (!block?.alive) continue;
      const cx = block.x + block.w / 2;
      const cy = block.y + block.h / 2;
      if (Math.abs(cx - origin.x) > reach || Math.abs(cy - origin.y) > reach) continue;
      if (!this.inCone(cx, cy) || !this.magnetic(i)) continue;
      seen.add(i);
      const cook = this.cook.get(i) ?? { t: 0, cracks: 0 };
      cook.t += dt;
      if (cook.cracks < 1 && cook.t >= 0.16) {
        world.crack(i, cx, cy);
        cook.cracks = 1;
        if (!cracked) {
          cracked = true;
          this.host.sfx("hitBlock");
        }
      } else if (cook.cracks < 2 && cook.t >= 0.34) {
        world.crack(i, cx, cy);
        cook.cracks = 2;
      }
      if (cook.t >= MAGNET_RIP && rips < MAGNET_RIP_PER_TICK && this.roomFor(block.w * block.h)) {
        if (this.ripBlock(i, dt)) {
          rips++;
          ripped = true;
        }
        this.cook.delete(i);
        continue;
      }
      this.cook.set(i, cook);
    }
    if (ripped) this.host.sfx("shatter");
    for (const id of this.cook.keys()) {
      if (seen.has(id)) continue;
      const cook = this.cook.get(id);
      if (!cook) continue;
      cook.t = Math.max(0, cook.t - dt * MAGNET_DECAY);
      if (cook.t <= 0) this.cook.delete(id);
    }
    this.glyphAcc = Math.min(3, this.glyphAcc + dt * MAGNET_GLYPH_RATE);
    let glyphs = 0;
    while (this.glyphAcc >= 1 && glyphs < 3) {
      if (!this.ripOneGlyph()) break;
      this.glyphAcc -= 1;
      glyphs++;
    }
    for (const slab of this.host.chunks.list.slice()) {
      if (!this.inCone(slab.x, slab.y)) continue;
      this.takeSlab(slab, true, dt);
    }
    const letters = this.host.letters;
    for (let i = letters.bodies.length - 1; i >= 0; i--) {
      const letter = letters.bodies[i];
      if (letter.pinned || !this.inCone(letter.x, letter.y)) continue;
      const area = Math.max(24, letter.w * letter.h);
      if (!this.roomFor(area)) {
        this.tugLetter(letter, dt);
        continue;
      }
      letters.bodies.splice(i, 1);
      this.bits.push(this.makeBit("letter", letter.x, letter.y, letter.a, letter.w, letter.h, 0.48, area, (Math.random() - 0.5) * 3, { letter }));
      this.clack();
    }
    for (let i = this.bodies.length - 1; i >= 0; i--) {
      const body = this.bodies[i];
      if (body.kind === "boss" || body.hp <= 0 || !this.inCone(body.x, body.y)) continue;
      const area = Math.max(80, body.w * body.h);
      if (!this.roomFor(area)) continue;
      this.bodies.splice(i, 1);
      body.wind = 0;
      body.vx = 0;
      body.vy = 0;
      this.bits.push(this.makeBit("minion", body.x, body.y, body.a, body.w, body.h, 1.8, area, 0, { body }));
      this.clack();
    }
    for (let i = this.coins.length - 1; i >= 0; i--) {
      const coin = this.coins[i];
      if (!this.inCone(coin.x, coin.y)) continue;
      const area = coin.gem ? 80 : 40;
      if (!this.roomFor(area)) continue;
      this.coins.splice(i, 1);
      this.bits.push(this.makeBit("coin", coin.x, coin.y, 0, 12, 12, coin.gem ? 0.55 : 0.22, area, coin.spin, { coin }));
      this.clack();
    }
    this.pullBits(dt);
  }

  private makeBit(
    kind: MagnetKind,
    x: number,
    y: number,
    a: number,
    w: number,
    h: number,
    mass: number,
    area: number,
    spin: number,
    extra: Partial<MagnetBit>,
  ): MagnetBit {
    const slot = this.packSlot(this.bits.length, this.bits.length + 1);
    return { kind, x, y, a, ox: slot.x, oy: slot.y, w, h, mass, area, spin, ...extra };
  }

  private ripBlock(index: number, dt: number): boolean {
    const block = this.host.world.blocks[index];
    if (!block?.alive) return false;
    const stirAt = this.stirs.findIndex((stir) => stir.index === index);
    if (stirAt >= 0) this.stirs.splice(stirAt, 1);
    this.releaseLoot(index, block.x + block.w / 2, block.y + block.h / 2);
    const before = this.bits.length;
    this.stealGlyphs(block.x, block.y, block.w, block.h, 4);
    const slab = this.lift(index);
    if (!slab) return this.bits.length > before;
    return this.takeSlab(slab, false, dt) || this.bits.length > before;
  }

  private takeSlab(slab: Slab, fromList: boolean, dt: number): boolean {
    if (fromList) {
      const at = this.host.chunks.list.indexOf(slab);
      if (at >= 0) this.host.chunks.list.splice(at, 1);
    }
    const pieces = this.breakSlab(slab, 0);
    let got = false;
    for (const piece of pieces) {
      if (this.capturePiece(piece)) got = true;
      else {
        if (!this.host.chunks.list.includes(piece)) this.host.chunks.list.push(piece);
        this.tugSlab(piece, dt);
      }
    }
    return got;
  }

  /** Split oversized and medium slabs into chunks the cone can hold. */
  private breakSlab(slab: Slab, depth: number): Slab[] {
    const area = Math.max(1, slab.bw * slab.bh);
    const over = slab.bw > 110 || slab.bh > 84 || area > 5200;
    const medium = !slab.fragment && (area >= 900 || slab.bw > 64 || slab.bh > 48);
    if (depth >= 2 || (!over && !medium)) return [slab];
    const list = this.host.chunks.list;
    if (!list.includes(slab)) list.push(slab);
    const made = this.host.chunks.shatter(slab, slab.x, slab.y, 36);
    for (const piece of made) {
      const at = list.indexOf(piece);
      if (at >= 0) list.splice(at, 1);
    }
    if (!made.length) return [slab];
    const out: Slab[] = [];
    for (const piece of made) out.push(...this.breakSlab(piece, depth + 1));
    return out;
  }

  private capturePiece(slab: Slab): boolean {
    const area = Math.max(1, slab.bw * slab.bh);
    if (slab.bw > 110 || slab.bh > 84 || area > 5200 || !this.roomFor(area)) return false;
    const at = this.host.chunks.list.indexOf(slab);
    if (at >= 0) this.host.chunks.list.splice(at, 1);
    this.bits.push(this.makeBit(
      "slab",
      slab.x,
      slab.y,
      slab.a,
      slab.bw,
      slab.bh,
      Math.max(0.5, Math.min(1.35, area / 1400)),
      area,
      (Math.random() - 0.5) * 2,
      { slab },
    ));
    this.clack();
    return true;
  }

  private stealGlyphs(x: number, y: number, w: number, h: number, limit: number): number {
    const letters = this.host.letters;
    const ids: number[] = [];
    letters.query(x, y, x + w, y + h, (i) => {
      if (ids.length >= limit) return;
      const cx = letters.x[i] + letters.w[i] / 2;
      const cy = letters.y[i] + letters.h[i] / 2;
      if (cx < x || cy < y || cx > x + w || cy > y + h) return;
      ids.push(i);
    });
    let n = 0;
    for (const i of ids) if (this.takeGlyph(i)) n++;
    return n;
  }

  private ripOneGlyph(): boolean {
    const letters = this.host.letters;
    const origin = this.magnetOrigin();
    const reach = MAGNET_RANGE;
    let best = -1;
    let bestD = reach + 1;
    letters.query(origin.x - reach, origin.y - reach, origin.x + reach, origin.y + reach, (i) => {
      const x = letters.x[i] + letters.w[i] / 2;
      const y = letters.y[i] + letters.h[i] / 2;
      if (!this.inCone(x, y)) return;
      const dist = Math.hypot(x - origin.x, y - origin.y);
      if (dist < bestD) {
        bestD = dist;
        best = i;
      }
    });
    return best >= 0 && this.takeGlyph(best);
  }

  private takeGlyph(i: number): boolean {
    if (!this.roomFor(32)) return false;
    const letters = this.host.letters;
    const body = letters.detach(i, 0, -30, -1, false, {
      vx: 0, vy: -30, av: (Math.random() - 0.5) * 6, burn: -1, pinned: false,
    });
    if (!body) return false;
    const at = letters.bodies.indexOf(body);
    if (at >= 0) letters.bodies.splice(at, 1);
    const area = Math.max(24, body.w * body.h);
    this.bits.push(this.makeBit(
      "letter", body.x, body.y, 0, body.w, body.h, 0.48, area, (Math.random() - 0.5) * 3, { letter: body },
    ));
    this.clack();
    return true;
  }

  private nearestBlocks(limit: number): number[] {
    const origin = this.magnetOrigin();
    const world = this.host.world;
    const scored: { i: number; d: number }[] = [];
    for (let i = 0; i < world.blocks.length; i++) {
      if (!this.magnetic(i)) continue;
      const block = world.blocks[i];
      const cx = block.x + block.w / 2;
      const cy = block.y + block.h / 2;
      if (!this.inCone(cx, cy)) continue;
      scored.push({ i, d: Math.hypot(cx - origin.x, cy - origin.y) });
    }
    scored.sort((a, b) => a.d - b.d);
    return scored.slice(0, limit).map((item) => item.i);
  }

  /** A quick tap. One or two pieces fly a short way and burst like a rocket. */
  private tapPulse() {
    const picks = this.nearestBlocks(4);
    for (const index of picks) {
      if (this.bits.length >= 2) break;
      const block = this.host.world.blocks[index];
      if (!block?.alive) continue;
      const area = block.w * block.h;
      const small = area <= 2200 && block.w <= 88 && block.h <= 64;
      if (small) this.ripBlock(index, 0.016);
      else this.biteBlock(index);
    }
    if (this.bits.length < 1) this.ripOneGlyph();
    if (this.bits.length < 2) this.ripOneGlyph();
    if (this.bits.length > 2) {
      const extra = this.bits.splice(2);
      for (const bit of extra) this.settleBit(bit, (Math.random() - 0.5) * 80, -60);
    }
    if (!this.bits.length) {
      this.pulseOnly();
      return;
    }
    this.throwClump(true);
  }

  private biteBlock(index: number): boolean {
    const block = this.host.world.blocks[index];
    if (!block?.alive) return false;
    const origin = this.magnetOrigin();
    const cx = block.x + block.w / 2;
    const cy = block.y + block.h / 2;
    this.host.world.crack(index, cx, cy);
    const dx = cx - origin.x;
    const dy = cy - origin.y;
    const dist = Math.hypot(dx, dy) || 1;
    const px = origin.x + (dx / dist) * Math.min(dist, MAGNET_RANGE * 0.7);
    const py = origin.y + (dy / dist) * Math.min(dist, MAGNET_RANGE * 0.7);
    const list = this.host.chunks.list;
    const before = list.length;
    this.host.chunks.spall(px, py, 18, 2, 6, 10);
    const born = list.splice(before);
    this.blastPage(px, py, 14, 36, 0, false, 0);
    if (!born.length) return false;
    const piece = born[0];
    for (const rest of born.slice(1)) list.push(rest);
    return this.capturePiece(piece);
  }

  private pulseOnly() {
    const origin = this.magnetOrigin();
    const aim = this.host.aim;
    const x = origin.x + Math.cos(aim) * 52;
    const y = origin.y + Math.sin(aim) * 52;
    this.blastPage(x, y, 18, 40, 0, false, 0);
    this.boom(x, y, 22, 0.2, false);
    this.fieldT = 0.28;
    this.magnetCd = 0.45;
    this.host.sfx("click");
  }

  private tugSlab(slab: Slab, dt: number) {
    const origin = this.magnetOrigin();
    const dx = origin.x - slab.x;
    const dy = origin.y - slab.y;
    const dist = Math.hypot(dx, dy) || 1;
    slab.vx += (dx / dist) * 900 * dt;
    slab.vy += (dy / dist) * 900 * dt - GRAVITY * dt;
  }

  private tugLetter(letter: LetterBody, dt: number) {
    const origin = this.magnetOrigin();
    const dx = origin.x - letter.x;
    const dy = origin.y - letter.y;
    const dist = Math.hypot(dx, dy) || 1;
    letter.vx += (dx / dist) * 980 * dt;
    letter.vy += (dy / dist) * 980 * dt - GRAVITY * 0.82 * dt;
  }

  private pullBits(dt: number) {
    const aim = this.host.aim;
    const origin = this.magnetOrigin();
    const ca = Math.cos(aim);
    const sa = Math.sin(aim);
    const k = 1 - Math.exp(-MAGNET_PULL * dt);
    const count = this.bits.length;
    for (let i = 0; i < count; i++) {
      const bit = this.bits[i];
      const slot = this.packSlot(i, count);
      bit.ox = slot.x;
      bit.oy = slot.y;
      const tx = origin.x + ca * (26 + bit.ox) - sa * bit.oy;
      const ty = origin.y + sa * (26 + bit.ox) + ca * bit.oy;
      bit.x += (tx - bit.x) * k;
      bit.y += (ty - bit.y) * k;
      const spin = aim * 0.15 + bit.spin * 0.2;
      bit.a += (spin - bit.a) * Math.min(1, dt * 8);
    }
  }

  private throwClump(tap: boolean) {
    const bits = this.bits.splice(0, this.bits.length);
    const weight = tap ? Math.min(0.18, this.weightOf(bits)) : this.weightOf(bits);
    const aim = this.host.aim;
    const speed = tap ? MAGNET_TAP_SPEED : MAGNET_SPEED0 + (MAGNET_SPEED1 - MAGNET_SPEED0) * weight;
    const loft = tap ? 30 : (1 - weight) * MAGNET_LOFT;
    const origin = this.magnetOrigin();
    const x = origin.x + Math.cos(aim) * 18;
    const y = origin.y + Math.sin(aim) * 18;
    const aimX = this.host.lookX ?? x + Math.cos(aim) * 220;
    const aimY = this.host.lookY ?? y + Math.sin(aim) * 220;
    const toss: MagnetToss = {
      x,
      y,
      vx: Math.cos(aim) * speed + this.host.vx * 0.25,
      vy: Math.sin(aim) * speed - loft + this.host.vy * 0.15,
      a: aim,
      av: (0.6 - weight) * 5,
      bits,
      weight,
      dmg: tap ? MAGNET_TAP_DMG : MAGNET_DMG0 + (MAGNET_DMG1 - MAGNET_DMG0) * weight,
      splash: tap ? MAGNET_TAP_SPLASH : MAGNET_SPLASH0 + (MAGNET_SPLASH1 - MAGNET_SPLASH0) * weight,
      radius: tap ? MAGNET_TAP_R : MAGNET_R0 + (MAGNET_R1 - MAGNET_R0) * weight,
      age: 0,
      bounces: 0,
      grav: tap ? MAGNET_TAP_GRAV : MAGNET_GRAV0 + (MAGNET_GRAV1 - MAGNET_GRAV0) * weight,
      sats: !tap && weight >= 0.8,
      tap,
      aimX,
      aimY,
      aimLeft: 0,
      pen: 0,
      dug: 0,
      digging: false,
      met: new Set(),
      struck: 0,
      bored: new Set(),
      wide: new Map(),
      cut: new Map(),
    };
    this.placeBits(toss);
    this.tosses.push(toss);
    this.magnetCd = tap ? 0.7 : MAGNET_CD;
    this.host.markShot();
    this.host.sfx(tap ? "click" : "shotgun");
  }

  private placeBits(toss: MagnetToss) {
    const ca = Math.cos(toss.a);
    const sa = Math.sin(toss.a);
    for (const bit of toss.bits) {
      bit.x = toss.x + ca * bit.ox - sa * bit.oy;
      bit.y = toss.y + sa * bit.ox + ca * bit.oy;
      bit.a = toss.a + bit.spin * 0.15;
    }
  }

  private stepTosses(dt: number) {
    const world = this.host.world;
    for (let i = this.tosses.length - 1; i >= 0; i--) {
      const toss = this.tosses[i];
      toss.age += dt;
      const x0 = toss.x;
      const y0 = toss.y;
      if (!toss.digging) toss.vy = Math.min(1400, toss.vy + toss.grav * dt);
      const speed = Math.hypot(toss.vx, toss.vy) || 1;
      let step = speed * dt;
      if (toss.digging) step = Math.min(step, Math.max(0, toss.aimLeft));
      toss.x += (toss.vx / speed) * step;
      toss.y += (toss.vy / speed) * step;
      toss.a += toss.av * dt;
      this.placeBits(toss);
      const fell = toss.y > world.H + 120;
      const left = toss.digging && (fell || toss.x < -80 || toss.x > world.W + 80 || toss.y < -480);
      if (fell || left) {
        if (toss.digging) this.burstToss(toss);
        else for (const bit of toss.bits) this.settleBit(bit, toss.vx * 0.2, toss.vy);
        this.tosses.splice(i, 1);
        continue;
      }
      if (toss.age < 0.04) continue;
      const minion = this.nearMinion(toss.x, toss.y, 16 + toss.weight * 22) || this.tossHitsBody(x0, y0, toss.x, toss.y);
      if (minion && speed > 180) {
        this.burstToss(toss);
        this.tosses.splice(i, 1);
        continue;
      }
      if (toss.digging) {
        toss.dug += this.slugBore(toss, x0, y0, toss.x, toss.y);
        toss.aimLeft -= step;
        if (toss.aimLeft <= 0.5 || toss.dug >= toss.pen || toss.age > 4) {
          this.burstToss(toss);
          this.tosses.splice(i, 1);
        }
        continue;
      }
      const solid = this.clumpSolid(toss.x, toss.y, Math.atan2(toss.vy, toss.vx));
      if (solid && (speed > 260 || toss.bounces >= 1)) {
        if (this.startSlug(toss)) {
          toss.dug += this.slugBore(toss, x0, y0, toss.x, toss.y);
          if (toss.dug >= toss.pen) {
            this.burstToss(toss);
            this.tosses.splice(i, 1);
          }
        } else {
          this.burstToss(toss);
          this.tosses.splice(i, 1);
        }
        continue;
      }
      if (toss.age > 4) {
        this.burstToss(toss);
        this.tosses.splice(i, 1);
        continue;
      }
      if (solid && speed < 260 && toss.bounces < 1) {
        toss.bounces++;
        toss.vy *= -0.45;
        toss.vx *= 0.72;
        toss.y -= 3;
        this.host.sfx("click");
      }
    }
  }

  /**
   * Aim the slug at the cursor. A tap still stops at the cursor. A real throw
   * travels at least MAGNET_PAST px past the bite; the cursor can only add to that.
   */
  private startSlug(toss: MagnetToss): boolean {
    const dx = toss.aimX - toss.x;
    const dy = toss.aimY - toss.y;
    const dist = Math.hypot(dx, dy);
    const forward = dx * toss.vx + dy * toss.vy;
    const sp = Math.max(520, Math.hypot(toss.vx, toss.vy));
    const pen = toss.tap ? MAGNET_TAP_PEN : MAGNET_PEN0 + (MAGNET_PEN1 - MAGNET_PEN0) * toss.weight;
    if (toss.tap) {
      if (dist < 8 || forward <= 0) return false;
      toss.vx = (dx / dist) * sp;
      toss.vy = (dy / dist) * sp;
      toss.aimLeft = dist;
    } else if (dist >= 8 && forward > 0) {
      toss.vx = (dx / dist) * sp;
      toss.vy = (dy / dist) * sp;
      toss.aimLeft = this.clampRun(toss, dist >= MAGNET_PAST ? dist : MAGNET_PAST);
    } else {
      const spd = Math.hypot(toss.vx, toss.vy) || 1;
      toss.vx = (toss.vx / spd) * sp;
      toss.vy = (toss.vy / spd) * sp;
      toss.aimLeft = this.clampRun(toss, MAGNET_PAST);
    }
    toss.digging = true;
    toss.pen = pen;
    toss.dug = 0;
    return true;
  }

  /**
   * Keep the detonation on the page. The 60px minimum still holds when the
   * page has room; an upward throw stops at the edge instead of leaving it.
   */
  private clampRun(toss: MagnetToss, want: number): number {
    const world = this.host.world;
    const margin = 28;
    if (toss.y <= margin || toss.y >= world.H - margin) return 0;
    const spd = Math.hypot(toss.vx, toss.vy) || 1;
    const ux = toss.vx / spd;
    const uy = toss.vy / spd;
    let allow = want;
    if (uy < -0.02) allow = Math.min(allow, (toss.y - margin) / -uy);
    if (uy > 0.02) allow = Math.min(allow, (world.H - margin - toss.y) / uy);
    if (ux < -0.02) allow = Math.min(allow, (toss.x - 12) / -ux);
    if (ux > 0.02) allow = Math.min(allow, (world.W - 12 - toss.x) / ux);
    return Math.max(0, allow);
  }



  /** How far along this segment a block is first met. Smaller means the slug hits it sooner. */
  private boreOrder(x0: number, y0: number, x1: number, y1: number, index: number): number {
    const block = this.host.world.blocks[index];
    if (!block) return 0;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-4) return 0;
    const px = Math.max(block.x, Math.min(x0, block.x + block.w));
    const py = Math.max(block.y, Math.min(y0, block.y + block.h));
    return ((px - x0) * dx + (py - y0) * dy) / len2;
  }

  /**
   * Carve the slug's path. A tap still nicks every block once, too lightly to pop it.
   * A real throw hits the first block it opens, nicks the next two, and leaves the
   * rest for the blast. Returns the material crossed, in px: removed cells over the trench width.
   */
  private slugBore(toss: MagnetToss, x0: number, y0: number, x1: number, y1: number): number {
    const world = this.host.world;
    const radius = toss.tap ? MAGNET_TAP_BORE : MAGNET_BORE0 + (MAGNET_BORE1 - MAGNET_BORE0) * toss.weight;
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(1, Math.ceil(dist / 10));
    const opened = new Map<number, number>();
    let carved = 0;
    world.beginCarve();
    for (let s = 1; s <= n; s++) {
      const t = s / n;
      carved += world.carve(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, radius, 0.08, (_cx, _cy, _color, _mat, owner) => {
        if (owner >= 0 && !toss.met.has(owner) && !opened.has(owner)) {
          opened.set(owner, s * 8 + this.boreOrder(x0, y0, x1, y1, owner));
        }
      });
    }
    const owners = [...opened.entries()].sort((a, b) => a[1] - b[1] || a[0] - b[0]);
    for (const [owner] of owners) {
      toss.met.add(owner);
      const block = world.blocks[owner];
      let amount = MAGNET_TAP_BORE_DMG;
      if (!toss.tap) {
        const rank = toss.struck;
        toss.struck += 1;
        amount = rank === 0
          ? MAGNET_BORE_DMG
          : rank <= MAGNET_BORE_MORE
            ? MAGNET_BORE_NEXT
            : 0;
        if (amount > 0) amount = this.takeWide(toss.wide, owner, amount);
      }
      if (amount <= 0) continue;
      if (toss.tap) toss.cut.set(owner, (toss.cut.get(owner) ?? 0) + amount);
      toss.bored.add(owner);
      world.damageBlock(owner, amount, 0, -1, x1, y1);
    }
    const len = (carved * CELL * CELL) / (2 * radius);
    if (toss.tap || (toss.aimLeft > MAGNET_PAST + 18 && toss.dug + len < toss.pen - 18)) return len;
    const letters = this.host.letters;
    const reach = radius * 1.05;
    letters.query(x1 - reach, y1 - reach, x1 + reach, y1 + reach, (i) => {
      const gx = letters.x[i] + letters.w[i] / 2;
      const gy = letters.y[i] + letters.h[i] / 2;
      const ldx = gx - x1;
      const ldy = gy - y1;
      const gap = Math.hypot(ldx, ldy) || 1;
      if (gap > reach) return;
      const vx = (ldx / gap) * 420 + (Math.random() - 0.5) * 80;
      const vy = (ldy / gap) * 420 - 80;
      const av = (Math.random() - 0.5) * 20;
      letters.detach(i, vx, vy, -1, false, { vx, vy, av, burn: -1, pinned: false });
    });
    return len;
  }

  private clumpSolid(x: number, y: number, a: number): boolean {
    const world = this.host.world;
    if (world.matAt(x, y) >= SOLID) return true;
    return world.matAt(x + Math.cos(a) * 14, y + Math.sin(a) * 14) >= SOLID;
  }

  private nearMinion(x: number, y: number, reach: number): boolean {
    for (const body of this.bodies) {
      if (body.hp <= 0) continue;
      const pad = Math.min(body.w, body.h) * 0.25;
      if (Math.hypot(body.x - x, body.y - y) < reach + pad) return true;
    }
    return false;
  }

  private tossHitsBody(x0: number, y0: number, x1: number, y1: number): boolean {
    for (const body of this.bodies) {
      if (body.hp <= 0) continue;
      if (this.segBody(x0, y0, x1, y1, body, 8) >= 0) return true;
    }
    return false;
  }

  /** Thrown minions and the minions they strike. Scales with impact speed. A full throw kills 52 HP. */
  private impactDmg(speed: number): number {
    return Math.max(20, Math.min(120, speed * MAGNET_MINION_SCALE));
  }

  private burstToss(toss: MagnetToss) {
    const speed = Math.hypot(toss.vx, toss.vy);
    const carried: Body[] = [];
    for (const bit of toss.bits) {
      if (bit.kind !== "minion" || !bit.body) continue;
      bit.body.x = bit.x;
      bit.body.y = bit.y;
      bit.body.vx = toss.vx * 0.15;
      bit.body.vy = toss.vy * 0.15;
      bit.body.wind = 0;
      carried.push(bit.body);
    }
    const show = this.showRadius(toss);
    this.host.chunks.spall(toss.x, toss.y, show * 0.85, Math.round(4 + toss.weight * 8), 4, 9);
    const splashMul = toss.tap ? MAGNET_TAP_SPLASH_MUL : MAGNET_SPLASH_MUL;
    this.blastPage(
      toss.x, toss.y, toss.radius, toss.dmg, toss.splash, true, splashMul,
      toss.tap ? undefined : toss.bored,
      toss.tap ? undefined : toss.wide,
      toss.tap ? 1.9 : MAGNET_LETTER_MUL,
      false,
      toss.tap ? toss.cut : undefined,
    );
    if (!toss.tap && toss.weight >= 0.8) {
      this.furrowLetters(toss.x, toss.y, toss.radius, toss.vx, toss.vy);
    }
    this.boom(toss.x, toss.y, show, toss.weight, toss.sats);
    if (toss.sats) this.scrapRing(toss.x, toss.y, show, toss.tap ? undefined : toss.wide);
    const hit = toss.tap
      ? this.impactDmg(speed)
      : this.impactDmg(speed) + (MAGNET_HIT_FULL - this.impactDmg(speed)) * toss.weight;
    const shardHurt = { left: MAGNET_SHARD_HURT_CAP };
    this.hurt(toss.x, toss.y, Math.max(36, show * 1.15), hit);
    for (const body of carried) {
      if (body.hp <= 0) continue;
      if (!this.bodies.includes(body)) this.bodies.push(body);
      this.damageBody(body, hit, toss.vx, toss.vy, hit >= MINION_HP);
    }
    const shake = MAGNET_SHAKE0 + (MAGNET_SHAKE1 - MAGNET_SHAKE0) * toss.weight;
    this.host.shake(shake);
    if (toss.weight >= 0.4) this.host.freeze?.((3 + toss.weight * 2) / 60);
    this.host.sfx(toss.weight >= 0.55 ? "boom" : "boomSmall");
    this.host.sfx("shatter");
    const sparks = Math.round(18 + toss.weight * 30);
    for (let n = 0; n < sparks; n++) {
      const ang = (n / sparks) * Math.PI * 2 + Math.random() * 0.2;
      const sp = (140 + Math.random() * 380) * (0.6 + toss.weight);
      const life = 0.55 + Math.random() * 0.4;
      this.host.parts.spawn({
        x: toss.x, y: toss.y,
        vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp - 40,
        life, max: life, size: 1.5,
        color: n % 2 ? "#e7edf4" : "#ff445c",
        grav: 360, rubble: false, glow: true, kind: "spark",
      });
    }
    const drift = Math.round(10 + toss.weight * 12);
    for (let n = 0; n < drift; n++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = 50 + Math.random() * 160;
      const life = 0.7 + Math.random() * 0.3;
      this.host.parts.spawn({
        x: toss.x + Math.cos(ang) * show * 0.4,
        y: toss.y + Math.sin(ang) * show * 0.4,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp - 36,
        life, max: life, size: 1.3,
        color: n % 2 ? "#d5dde6" : "#ffb089",
        grav: 140, rubble: false, glow: true, kind: "spark",
      });
    }
    const dust = 7 + Math.round(toss.weight * 4);
    for (let n = 0; n < dust; n++) {
      const ang = (n / dust) * Math.PI * 2 + 0.35;
      const dist = show * (0.72 + (n % 3) * 0.16);
      this.host.parts.smoke(
        toss.x + Math.cos(ang) * dist,
        toss.y + Math.sin(ang) * dist,
        Math.cos(ang) * (16 + Math.random() * 28),
        Math.sin(ang) * 14 - 26 - Math.random() * 24,
        (1.15 + Math.random() * 0.7) * CELL,
        0.85 + Math.random() * 0.25,
        n % 2 ? "#b7c0c8" : "#cbb9a4",
      );
    }
    for (const bit of toss.bits) {
      if (bit.kind === "minion") continue;
      if (bit.kind === "coin" && bit.coin) {
        bit.coin.x = bit.x;
        bit.coin.y = bit.y;
        bit.coin.vx = toss.vx * 0.2 + (Math.random() - 0.5) * 320;
        bit.coin.vy = -160 - Math.random() * 220;
        bit.coin.life = Math.max(8, bit.coin.life);
        this.coins.push(bit.coin);
        continue;
      }
      const ang = Math.random() * Math.PI * 2;
      const sp = 180 + Math.random() * 420 * (0.4 + toss.weight);
      this.shards.push({
        bit,
        vx: Math.cos(ang) * sp + toss.vx * 0.15,
        vy: Math.sin(ang) * sp - 120 + toss.vy * 0.1,
        av: (Math.random() - 0.5) * 14,
        age: 0,
        boomed: false,
        wide: toss.tap ? undefined : toss.wide,
        hurt: shardHurt,
      });
    }
  }

  /** Ring and dust radius. A full clump still reads as the old 54px burst. */
  private showRadius(toss: MagnetToss): number {
    if (toss.tap) return toss.radius;
    return MAGNET_R0 + (MAGNET_FX_R1 - MAGNET_R0) * toss.weight;
  }

  /**
   * Shockwave ahead of a full clump: knocks off the nearest glyphs along the flight
   * (outside the blast reach), fewer for an uphill throw, and pops a few small
   * elements ahead. The count is fixed, so it does not depend on where the burst lands.
   */
  private furrowLetters(x: number, y: number, radius: number, dirX: number, dirY: number) {
    const spd = Math.hypot(dirX, dirY) || 1;
    const ux = dirX / spd;
    const uy = dirY / spd;
    const near = radius * MAGNET_LETTER_MUL;
    const letters = this.host.letters;
    const found: { i: number; d: number }[] = [];
    const total = letters.x.length;
    for (let i = 0; i < total; i++) {
      if (!letters.alive[i]) continue;
      const dx = letters.x[i] + letters.w[i] / 2 - x;
      const dy = letters.y[i] + letters.h[i] / 2 - y;
      const ahead = dx * ux + dy * uy;
      if (ahead <= 0) continue;
      const dist = Math.hypot(dx, dy);
      if (dist <= near || dist > MAGNET_FURROW_D) continue;
      const side = Math.abs(dx * uy - dy * ux);
      found.push({ i, d: ahead + side * MAGNET_FURROW_SIDE });
    }
    this.furrowPop(x, y, near, ux, uy);
    found.sort((a, b) => a.d - b.d);
    const n = Math.min(Math.round(MAGNET_FURROW_N * (1 + MAGNET_FURROW_FALL * Math.min(0, uy))), found.length);
    for (let k = 0; k < n; k++) {
      const i = found[k].i;
      const dx = letters.x[i] + letters.w[i] / 2 - x;
      const dy = letters.y[i] + letters.h[i] / 2 - y;
      const dist = Math.hypot(dx, dy) || 1;
      const push = 520 * (1 - dist / (MAGNET_FURROW_D * 1.4));
      const vx = (dx / dist) * push + ux * 220 + (Math.random() - 0.5) * 90;
      const vy = (dy / dist) * push + uy * 220 - 160 - Math.random() * 140;
      const av = (Math.random() - 0.5) * 40;
      letters.detach(i, vx, vy, -1, false, { vx, vy, av, burn: -1, pinned: false });
    }
  }

  private furrowPop(x: number, y: number, near: number, ux: number, uy: number) {
    const world = this.host.world;
    const picks: { i: number; d: number }[] = [];
    world.blocks.forEach((block, i) => {
      if (!block.alive || !block.canDetach || i === this.bossIndex || block.mark === "logo") return;
      if (block.w * block.h > MAGNET_FURROW_POP_AREA) return;
      const dx = block.x + block.w / 2 - x;
      const dy = block.y + block.h / 2 - y;
      const ahead = dx * ux + dy * uy;
      const dist = Math.hypot(dx, dy);
      if (ahead <= 0 || dist <= near || dist > MAGNET_FURROW_POP_D) return;
      picks.push({ i, d: ahead + Math.abs(dx * uy - dy * ux) * MAGNET_FURROW_SIDE });
    });
    picks.sort((a, b) => a.d - b.d);
    for (const { i } of picks.slice(0, MAGNET_FURROW_POP)) {
      const block = world.blocks[i];
      if (!block?.alive) continue;
      world.damageBlock(i, Math.max(1, block.hp - block.damage + 1), ux, uy, x, y);
    }
  }

  private wideBand(index: number): boolean {
    const block = this.host.world.blocks[index];
    return !!block && block.w * block.h > MAGNET_WIDE_AREA;
  }

  /**
   * Page-wide band: the whole throw stops at MAGNET_WIDE_TOTAL (one rocket hit).
   * A graze (nick) freezes that band at MAGNET_BORE_NEXT
   * so a later shard cannot finish a hero the blast only clipped.
   */
  private takeWide(wide: Map<number, number> | undefined, index: number, amount: number, nick = false): number {
    if (!wide || amount <= 0) return amount;
    if (!this.wideBand(index)) return amount;
    const raw = wide.get(index) ?? 0;
    const nicked = nick || raw >= WIDE_NICK;
    const used = raw & (WIDE_NICK - 1);
    const block = this.host.world.blocks[index];
    const huge = !!block && block.w * block.h > MAGNET_HUGE_AREA;
    const ceiling = huge ? MAGNET_HUGE_TOTAL : nicked ? MAGNET_BORE_NEXT : MAGNET_WIDE_TOTAL;
    const flag = nicked ? WIDE_NICK : 0;
    const room = ceiling - used;
    if (room <= 0) {
      wide.set(index, used + flag);
      return 0;
    }
    let dealt = Math.min(amount, room);
    if (huge && block) dealt = Math.min(dealt, Math.max(0, block.hp - block.damage - 1));
    wide.set(index, used + dealt + flag);
    return dealt;
  }

  private scrapRing(x: number, y: number, radius: number, wide?: Map<number, number>) {
    const dist = radius + MAGNET_SAT_R * 0.82;
    for (let i = 0; i < MAGNET_SAT_N; i++) {
      const ang = (i / MAGNET_SAT_N) * Math.PI * 2 + 0.35;
      this.blastPage(
        x + Math.cos(ang) * dist,
        y + Math.sin(ang) * dist,
        MAGNET_SAT_R,
        MAGNET_SAT_DMG,
        0,
        true,
        0,
        undefined,
        wide,
      );
    }
  }

  private boom(x: number, y: number, radius: number, weight: number, full: boolean) {
    this.host.bloom?.(x, y, radius, weight);
    const life = 0.78 + weight * 0.18;
    this.arcs.push({ x, y, life, max: life, r0: radius * 0.72, r1: radius * (2.15 + weight * 0.45), weight });
    if (full) {
      const inner = 0.68 + weight * 0.16;
      this.arcs.push({ x, y, life: inner, max: inner, r0: radius * 0.5, r1: radius * (1.45 + weight * 0.2), weight });
    }
  }

  /** Full capped hit only when the blast center is inside the band or in the margin. */
  private wideAmount(index: number, x: number, y: number, amount: number): { amount: number; nick: boolean } {
    if (!this.wideBand(index)) return { amount, nick: false };
    const block = this.host.world.blocks[index];
    const dx = Math.max(block.x - x, 0, x - (block.x + block.w));
    const dy = Math.max(block.y - y, 0, y - (block.y + block.h));
    if (Math.hypot(dx, dy) > MAGNET_WIDE_GAP) return { amount: Math.min(amount, MAGNET_BORE_NEXT), nick: true };
    return { amount, nick: false };
  }

  private blastPage(x: number, y: number, radius: number, dmg: number, splash: number, scorch: boolean, splashMul = MAGNET_SPLASH_MUL, already?: Set<number>, wide?: Map<number, number>, letterMul = 1.9, forceNick = false, credit?: Map<number, number>) {
    const world = this.host.world;
    const owners = new Set<number>();
    const dealt = (index: number, raw: number) => {
      const amount = credit ? Math.max(0, raw - (credit.get(index) ?? 0)) : raw;
      if (!wide) return amount;
      const hit = this.wideAmount(index, x, y, amount);
      return this.takeWide(wide, index, hit.amount, hit.nick || forceNick);
    };
    world.beginCarve();
    world.carve(x, y, radius, 0.16, (_cx, _cy, _color, _mat, owner) => {
      if (owner >= 0) owners.add(owner);
    });
    if (scorch) world.scorch(x, y, radius * 0.92, radius * 1.7, 1);
    const letters = this.host.letters;
    const reach = radius * letterMul;
    letters.query(x - reach, y - reach, x + reach, y + reach, (i) => {
      const gx = letters.x[i] + letters.w[i] / 2;
      const gy = letters.y[i] + letters.h[i] / 2;
      const dx = gx - x;
      const dy = gy - y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > reach) return;
      const fall = dist / reach;
      const impulse = 1050 * (1 - fall) ** 0.7 + 110;
      const vx = (dx / dist) * impulse + (Math.random() - 0.5) * 120;
      const vy = (dy / dist) * impulse - (120 + Math.random() * 200);
      const av = (Math.random() - 0.5) * 56;
      const burn = dist < radius * 1.05 ? Math.random() * 0.25 : -1;
      letters.detach(i, vx, vy, burn, false, { vx, vy, av, burn, pinned: false });
    });
    for (const body of letters.bodies) {
      const dx = body.x - x;
      const dy = body.y - y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > radius * 2.3) continue;
      const push = 900 * (1 - dist / (radius * 2.3));
      body.vx += (dx / dist) * push;
      body.vy += (dy / dist) * push - 150;
      body.av += (Math.random() - 0.5) * 40;
    }
    for (const slab of this.host.chunks.list.slice()) {
      const dx = slab.x - x;
      const dy = slab.y - y;
      const dist = Math.hypot(dx, dy) || 1;
      const limit = radius * 1.4 + Math.max(slab.bw, slab.bh) * 0.5;
      if (dist > limit) continue;
      if (!slab.fragment) this.host.chunks.shatter(slab, x, y, 420);
      else {
        const push = 700 * (1 - dist / limit);
        slab.vx += (dx / dist) * push;
        slab.vy += (dy / dist) * push - 200;
        slab.av += (Math.random() - 0.5) * 20;
      }
    }
    const trenchOnly = (index: number) => !!already?.has(index) && !this.wideBand(index);
    for (const owner of owners) {
      if (trenchOnly(owner)) continue;
      world.damageBlock(owner, dealt(owner, dmg), 0, -1, x, y);
    }
    const splashReach = radius * splashMul;
    if (splash <= 0 || splashReach <= 0) return;
    world.blocks.forEach((block, index) => {
      if (!block.alive || owners.has(index) || trenchOnly(index)) return;
      const dx = Math.max(block.x - x, 0, x - (block.x + block.w));
      const dy = Math.max(block.y - y, 0, y - (block.y + block.h));
      const dist = Math.hypot(dx, dy);
      if (dist < splashReach) world.damageBlock(index, dealt(index, splash * (1 - dist / splashReach)), 0, -1, x, y);
    });
  }

  private stepShards(dt: number) {
    const world = this.host.world;
    for (let i = this.shards.length - 1; i >= 0; i--) {
      const shard = this.shards[i];
      shard.age += dt;
      shard.vy = Math.min(1200, shard.vy + GRAVITY * dt);
      shard.bit.x += shard.vx * dt;
      shard.bit.y += shard.vy * dt;
      shard.bit.a += shard.av * dt;
      const speed = Math.hypot(shard.vx, shard.vy);
      const solid = world.matAt(shard.bit.x, shard.bit.y) >= SOLID;
      const minion = this.nearMinion(shard.bit.x, shard.bit.y, 18);
      if (!shard.boomed && shard.age > 0.08 && speed > 150 && (solid || minion)) {
        shard.boomed = true;
        this.blastPage(shard.bit.x, shard.bit.y, MAGNET_SHARD_R, MAGNET_SHARD_BLOCK, MAGNET_SHARD_BLOCK * 0.6, false, MAGNET_SPLASH_MUL, undefined, shard.wide);
        const struck = this.bodies.some((body) => body.hp > 0 && this.boxDist(body, shard.bit.x, shard.bit.y) <= 22);
        const sting = Math.min(MAGNET_SHARD_HURT, shard.hurt.left);
        if (struck && sting > 0) {
          shard.hurt.left -= sting;
          this.hurt(shard.bit.x, shard.bit.y, 22, sting);
        }
        this.host.shake(0.06);
        this.clack();
        shard.vx *= 0.35;
        shard.vy *= -0.25;
      } else if (solid && shard.vy > 0) {
        shard.vy *= -0.3;
        shard.vx *= 0.7;
        shard.bit.y -= 2;
      }
      if ((speed < 80 && solid && shard.age > 0.2) || shard.age > 2.4 || shard.bit.y > world.H + 80) {
        this.settleBit(shard.bit, shard.vx, shard.vy);
        this.shards.splice(i, 1);
      }
    }
    if (this.shards.length > 48) {
      const old = this.shards.shift();
      if (old) this.settleBit(old.bit, old.vx, old.vy);
    }
  }

  private settleBit(bit: MagnetBit, vx: number, vy: number) {
    if (bit.kind === "slab" && bit.slab) {
      bit.slab.x = bit.x;
      bit.slab.y = bit.y;
      bit.slab.a = bit.a;
      bit.slab.vx = vx;
      bit.slab.vy = vy;
      bit.slab.av = bit.spin;
      this.host.chunks.list.push(bit.slab);
      return;
    }
    if (bit.kind === "letter" && bit.letter) {
      bit.letter.x = bit.x;
      bit.letter.y = bit.y;
      bit.letter.a = bit.a;
      bit.letter.vx = vx;
      bit.letter.vy = vy;
      bit.letter.av = bit.spin * 4;
      this.host.letters.bodies.push(bit.letter);
      return;
    }
    if (bit.kind === "coin" && bit.coin) {
      bit.coin.x = bit.x;
      bit.coin.y = bit.y;
      bit.coin.vx = vx;
      bit.coin.vy = vy;
      this.coins.push(bit.coin);
      return;
    }
    if (bit.kind === "minion" && bit.body) {
      bit.body.x = bit.x;
      bit.body.y = bit.y;
      bit.body.vx = vx;
      bit.body.vy = vy;
      bit.body.wind = 0;
      this.bodies.push(bit.body);
    }
  }

  private drawCone(ctx: CanvasRenderingContext2D, alpha: number) {
    const origin = this.magnetOrigin();
    const aim = this.host.aim;
    ctx.beginPath();
    ctx.moveTo(origin.x, origin.y);
    ctx.arc(origin.x, origin.y, MAGNET_RANGE, aim - MAGNET_HALF, aim + MAGNET_HALF);
    ctx.closePath();
    ctx.fillStyle = `rgba(226, 59, 59, ${alpha.toFixed(3)})`;
    ctx.fill();
    ctx.strokeStyle = "#ffd0d0";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(origin.x, origin.y, MAGNET_RANGE * 0.45, aim - MAGNET_HALF * 0.7, aim + MAGNET_HALF * 0.7);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(origin.x, origin.y, MAGNET_RANGE * 0.75, aim - MAGNET_HALF * 0.85, aim + MAGNET_HALF * 0.85);
    ctx.stroke();
  }

  private drawDeny(ctx: CanvasRenderingContext2D) {
    const origin = this.magnetOrigin();
    ctx.beginPath();
    ctx.strokeStyle = `rgba(255, 208, 208, ${(this.denyT / 0.16).toFixed(3)})`;
    ctx.arc(origin.x, origin.y, 10, 0, Math.PI * 2);
    ctx.stroke();
  }

  private drawCook(ctx: CanvasRenderingContext2D) {
    const world = this.host.world;
    const z = world.Z;
    for (const [index, cook] of this.cook) {
      const block = world.blocks[index];
      if (!block?.alive) continue;
      const j = (Math.sin(this.t * 48 + index) > 0 ? 1 : -1) * (1 + cook.t * 4);
      const k = (Math.cos(this.t * 41 + index) > 0 ? 1 : -1) * (1 + cook.t * 3);
      const x = Math.round(block.x);
      const y = Math.round(block.y);
      const w = Math.round(block.w);
      const h = Math.round(block.h);
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = "rgba(12, 8, 18, 0.45)";
      ctx.fillRect(x, y, w, h);
      world.pageCache.copy(ctx, block.x * z, block.y * z, block.w * z, block.h * z, x + j, y + k, w, h);
      world.textCache.copy(ctx, block.x * z, block.y * z, block.w * z, block.h * z, x + j, y + k, Math.round(block.w), Math.round(block.h));
      ctx.strokeStyle = cook.t > 0.3 ? "#ffffff" : "#ffd0d0";
      ctx.lineWidth = 2;
      ctx.strokeRect(x + j, y + k, Math.round(block.w), Math.round(block.h));
      ctx.restore();
    }
  }

  private drawBit(ctx: CanvasRenderingContext2D, bit: MagnetBit) {
    if (bit.kind === "minion" && bit.body) {
      bit.body.x = bit.x;
      bit.body.y = bit.y;
      bit.body.a = bit.a * 0.15;
      this.drawBody(ctx, bit.body);
      return;
    }
    if (bit.kind === "coin" && bit.coin) {
      bit.coin.x = bit.x;
      bit.coin.y = bit.y;
      this.drawCoin(ctx, bit.coin);
      return;
    }
    const cap = 64 / Math.max(bit.w, bit.h, 1);
    const scale = Math.min(1, cap);
    ctx.save();
    ctx.translate(bit.x, bit.y);
    ctx.rotate(bit.a);
    ctx.imageSmoothingEnabled = false;
    if (bit.kind === "slab" && bit.slab) {
      const slab = bit.slab;
      ctx.drawImage(slab.canvas, slab.bx * scale, slab.by * scale, slab.bw * scale, slab.bh * scale);
    } else if (bit.letter) {
      const letter = bit.letter;
      const w = letter.w + letter.pad * 2;
      const h = letter.h + letter.pad * 2;
      ctx.drawImage(letter.sprite, (-letter.w / 2 - letter.pad) * scale, (-letter.h / 2 - letter.pad) * scale, w * scale, h * scale);
    }
    ctx.restore();
  }

  private drawClumpAura(ctx: CanvasRenderingContext2D, bits: MagnetBit[], weight: number) {
    let x = 0;
    let y = 0;
    for (const bit of bits) {
      x += bit.x;
      y += bit.y;
    }
    x /= bits.length;
    y /= bits.length;
    const grow = 16 + Math.sqrt(bits.length) * 13 + weight * 8;
    const glow = ctx.createRadialGradient(x, y, 4, x, y, grow);
    glow.addColorStop(0, `rgba(255, 70, 82, ${(0.22 + weight * 0.28).toFixed(3)})`);
    glow.addColorStop(0.42, `rgba(160, 16, 36, ${(0.1 + weight * 0.12).toFixed(3)})`);
    glow.addColorStop(1, "rgba(40, 0, 8, 0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(x, y, grow, 0, Math.PI * 2);
    ctx.fill();
    const n = 3 + Math.min(7, bits.length);
    for (let i = 0; i < n; i++) {
      const a = this.t * (2.1 + i * 0.17) + i * 1.7;
      const rad = grow * (0.62 + (i % 3) * 0.14);
      ctx.save();
      ctx.translate(x + Math.cos(a) * rad, y + Math.sin(a) * rad * 0.86);
      ctx.rotate(a * 2);
      ctx.fillStyle = i % 2 ? "#d7dee8" : "#ff3b52";
      ctx.fillRect(-4, -1.5, 8, 3);
      ctx.restore();
    }
  }

  private drawArcs(ctx: CanvasRenderingContext2D) {
    for (const arc of this.arcs) {
      const k = arc.max > 0 ? arc.life / arc.max : 0;
      const expand = Math.min(1, (1 - k) / 0.22);
      const radius = arc.r0 + (arc.r1 - arc.r0) * expand;
      const fade = Math.min(1, k / 0.32);
      const teeth = 20 + Math.round(arc.weight * 8);
      this.strokeJagged(ctx, arc.x, arc.y, radius, 0.95 * fade, "#f7fbff", 7, teeth);
      this.strokeJagged(ctx, arc.x - 4, arc.y + 2, radius * 0.9, 0.72 * fade, "#ff3348", 4.5, Math.max(14, teeth - 5));
      this.strokeJagged(ctx, arc.x + 4, arc.y - 2, radius * 1.08, 0.55 * fade, "#7ee7ff", 3.6, teeth + 4);
    }
  }

  private strokeJagged(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    alpha: number,
    color: string,
    width: number,
    teeth: number,
  ) {
    if (alpha <= 0.02 || radius < 2) return;
    ctx.save();
    ctx.beginPath();
    const steps = Math.max(18, Math.round(radius / 5));
    for (let i = 0; i <= steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      const jag = 1 + Math.sin(a * teeth + this.t * 14) * 0.24 + (i % 3 === 0 ? 0.16 : 0) + (i % 5 === 0 ? -0.12 : 0);
      const px = x + Math.cos(a) * radius * jag;
      const py = y + Math.sin(a) * radius * jag;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.strokeStyle = color;
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    ctx.lineWidth = width;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.stroke();
    ctx.restore();
  }

  private lift(index: number): Slab | null {
    const before = this.host.chunks.list.length;
    this.host.chunks.detach(index, 0, -30, 0, false);
    const born = this.host.chunks.list.splice(before);
    let big: Slab | null = null;
    for (const slab of born) if (!big || slab.bw * slab.bh > big.bw * big.bh) big = slab;
    for (const slab of born) if (slab !== big) this.host.chunks.list.push(slab);
    return big;
  }

  private stepThrown(dt: number) {
    for (let i = this.thrown.length - 1; i >= 0; i--) {
      const body = this.thrown[i];
      body.age += dt;
      body.hit = Math.max(0, body.hit - dt);
      body.vy = Math.min(1100, body.vy + 1500 * dt);
      body.x += body.vx * dt;
      body.y += body.vy * dt;
      body.a += body.av * dt;
      const speed = Math.hypot(body.vx, body.vy);
      const solid = this.host.world.matAt(body.x, body.y) >= SOLID;
      if (body.launched && !body.dug && solid) {
        body.dug = true;
        this.punch.dig(body.x, body.y, PUNCH.minionCarve);
      }
      const struck = !(body.launched && body.age < 0.06) && this.thrownHit(body);
      if (body.hit <= 0 && speed > 200 && (solid || struck)) {
        body.hit = 0.22;
        const dmg = Math.min(170, speed * 0.14);
        this.smack(body.x, body.y, dmg);
        this.hurt(body.x, body.y, 36, dmg);
        this.host.shake(Math.min(0.2, speed / 6000));
        this.host.sfx("hitChunk");
        if (speed > 640) {
          this.noteDead(body);
          if (body.purse) this.drop(body.x, body.y, body.purse, false);
          if (body.launched && body.hp <= 0) this.host.sfx("minionDie");
          this.burst(body);
          this.thrown.splice(i, 1);
          continue;
        }
        if (solid) {
          body.vy *= -0.28;
          body.vx *= 0.55;
        }
      } else if (solid && body.vy > 0) {
        body.vy *= -0.25;
        body.vx *= 0.7;
        body.y -= 2;
      }
      if (speed < 90 && solid && body.age > 0.35) {
        this.settle(body);
        this.thrown.splice(i, 1);
        continue;
      }
      if (body.y > this.host.world.H + 80 || body.age > 8) {
        if (body.launched && body.hp <= 0) this.payLaunch(body);
        else this.settle(body);
        this.thrown.splice(i, 1);
      }
    }
    if (this.thrown.length > 6) {
      const old = this.thrown.shift();
      if (old) {
        if (old.launched && old.hp <= 0) this.payLaunch(old);
        else this.settle(old);
      }
    }
  }

  private payLaunch(body: Body) {
    if (body.purse) this.drop(body.x, body.y, body.purse, false);
    this.host.sfx("minionDie");
    this.burst(body);
  }

  private thrownHit(body: Body): boolean {
    for (const minion of this.bodies) {
      const reach = Math.max(16, Math.min(minion.w, minion.h) * 0.4);
      if (Math.hypot(minion.x - body.x, minion.y - body.y) < reach) return true;
    }
    return false;
  }

  private smack(x: number, y: number, dmg: number) {
    const world = this.host.world;
    for (let i = 0; i < world.blocks.length; i++) {
      const block = world.blocks[i];
      if (!block.alive) continue;
      const dx = Math.max(block.x - x, 0, x - (block.x + block.w));
      const dy = Math.max(block.y - y, 0, y - (block.y + block.h));
      const dist = Math.hypot(dx, dy);
      if (dist > 38 || block.w * block.h > 28000) continue;
      const cx = block.x + block.w / 2 - x;
      const cy = block.y + block.h / 2 - y;
      const len = Math.hypot(cx, cy) || 1;
      world.damageBlock(i, dmg * (1 - dist / 38), cx / len, cy / len, x, y);
    }
  }

  private wakeOne(): boolean {
    const world = this.host.world;
    const picks: number[] = [];
    for (let i = 0; i < world.blocks.length; i++) {
      const block = world.blocks[i];
      if (!block?.alive || !block.canDetach || block.children.length > 0) continue;
      if (i === this.bossIndex || this.stirs.some((stir) => stir.index === i)) continue;
      const area = block.w * block.h;
      if (area < 700 || area > 14000 || block.w < 14 || block.h < 14) continue;
      picks.push(i);
    }
    if (!picks.length) return false;
    const index = picks[Math.floor(Math.random() * picks.length)];
    const block = world.blocks[index];
    const kind: Body["kind"] = block.w * block.h < 1800 ? "crawl" : index % 3 === 0 ? "fly" : "hop";
    const purse = MINION_COINS;
    this.stirs.push({
      index, x: block.x, y: block.y, w: block.w, h: block.h,
      t: 0, dur: 0.66 + Math.random() * 0.3, kind, purse, dust: 0.05, cracked: 0,
    });
    this.host.sfx("spawn");
    return true;
  }

  private flop() {
    if (!this.bodies.length) return;
    for (const body of this.bodies) {
      body.kind = "thrown";
      body.eyes = false;
      body.vy = Math.min(body.vy, -20);
      this.thrown.push(body);
    }
    this.bodies.length = 0;
  }

  private stepMinions(dt: number) {
    const target = this.aggro();
    this.focus.x = target.x;
    this.focus.y = target.y;
    if (this.host.flaming) {
      const ax = this.host.x + Math.cos(this.host.aim) * 56;
      const ay = this.host.y - 22 + Math.sin(this.host.aim) * 56;
      this.hurt(ax, ay, 50, FLAME_DPS * dt);
    }
    for (let i = this.bodies.length - 1; i >= 0; i--) {
      const body = this.bodies[i];
      body.flash = Math.max(0, body.flash - dt);
      body.bar = Math.max(0, body.bar - dt);
      body.hit = Math.max(0, body.hit - dt);
      this.tickBlink(body, dt);
      if (body.phase !== "chase") {
        this.stepAwake(body, dt);
        if (this.bodies[i] !== body) continue;
        continue;
      }
      if (body.shoveT > 0) {
        body.shoveT = Math.max(0, body.shoveT - dt);
        body.vy = Math.min(980, body.vy + 1600 * dt);
        body.x += body.vx * dt;
        body.y += body.vy * dt;
        body.vx *= Math.max(0, 1 - dt * 0.8);
        body.x = Math.max(10, Math.min(this.host.world.W - 10, body.x));
        if (body.y > this.host.world.H + 48) this.kill(body);
        continue;
      }
      const prevWind = body.wind;
      body.wind = Math.max(0, body.wind - dt);
      body.shy = Math.max(0, body.shy - dt);
      const dx = target.x - body.x;
      const dy = target.y - body.y;
      const dist = Math.hypot(dx, dy) || 1;
      const retreat = body.shy > 0;
      const steerX = retreat ? -dx : dx;
      const steerY = retreat ? -dy : dy;
      const pace = this.pace();
      if (body.kind === "fly") {
        body.vx += (steerX / dist) * 380 * pace * dt;
        body.vy += (steerY / dist) * 380 * pace * dt;
        body.vx *= Math.max(0, 1 - dt * 1.5);
        body.vy *= Math.max(0, 1 - dt * 1.5);
        body.y += Math.sin(this.t * 6 + body.x * 0.02) * 20 * dt;
      } else {
        body.vy = Math.min(980, body.vy + 1600 * dt);
        const speed = (body.kind === "crawl" ? 78 : body.kind === "boss" ? 96 : 150) * pace;
        const dir = Math.sign(steerX) || (retreat ? -1 : 1);
        body.vx += (dir * speed - body.vx) * Math.min(1, dt * 3.2);
        const grounded = this.host.world.matAt(body.x, body.y + body.h * 0.45 + 3) >= SOLID && body.vy >= 0;
        if (grounded) {
          body.vy = 0;
          body.hop -= dt;
          if (body.hop <= 0 && body.kind !== "crawl") {
            body.hop = body.kind === "boss" ? 1.15 : 0.48 + Math.random() * 0.35;
            body.vy = body.kind === "boss" ? -340 : -430;
          } else if (body.kind === "crawl" && body.hop <= 0) {
            body.hop = 1.4;
            body.vy = -160;
          }
        }
      }
      if ((body.kind === "boss" || body.affix === "tank") && !retreat && body.chargeT <= 0) {
        if (body.wind <= 0) body.slam -= dt;
        const slamReach = body.phase2 && body.gate ? 200 : 168;
        const slamHit = body.phase2 && body.gate ? 175 : 150;
        if (body.wind <= 0 && body.slam <= 0 && dist < slamReach) {
          body.slam = body.phase2 ? 1.8 : 2.5;
          body.wind = 0.42;
        } else if (prevWind > 0 && body.wind <= 0) {
          const pd = Math.hypot(this.host.x - body.x, this.host.y - 24 - body.y);
          if (pd < slamHit && this.grace <= 0) this.shove(body, 1000, -300, 0.55, 0.5, this.hazard ? this.biteDamage(true) : 0);
        }
      }
      if (body.affix && body.gate && body.phase === "chase" && !retreat) {
        this.stepAffix(body, dt, dist, dx, dy, pace);
      }
      body.shieldT = Math.max(0, body.shieldT - dt);
      if (body.chargeT > 0) {
        body.chargeT -= dt;
        body.vx = body.chargeDirX * (body.phase2 ? 520 : 420) * pace;
        body.vy = body.chargeDirY * (body.phase2 ? 420 : 320) * pace;
        if (body.chargeT <= 0) {
          body.vx *= 0.3;
          body.vy *= 0.3;
          body.skillCd = body.phase2 ? 1.6 : 2.4;
        }
      }
      body.x += body.vx * dt;
      body.y += body.vy * dt;
      body.x = Math.max(10, Math.min(this.host.world.W - 10, body.x));
      if (body.y > this.host.world.H + 48) {
        this.kill(body);
        continue;
      }
      const reach = (body.kind === "boss" ? 26 : 14) + Math.min(body.w, body.h) * 0.18;
      const pd = Math.hypot(this.host.x - body.x, this.host.y - 24 - body.y);
      if (pd < reach && this.grace <= 0 && body.shy <= 0) {
        this.shove(body, 760 * pace, -420, 0.4, 0.28, this.hazard ? this.biteDamage(false) : 0);
      }
    }
  }

  private stepAffix(body: Body, dt: number, dist: number, dx: number, dy: number, pace: number) {
    if (body.affix === "summoner") {
      body.skillCd -= dt;
      const cap = body.phase2 ? (this.gateBossFloor ? 6 : 5) : (this.gateBossFloor ? 4 : 3);
      if (body.skillCd <= 0 && body.adds < cap) {
        const wave = body.phase2 ? (this.gateBossFloor ? 4 : 3) : (this.gateBossFloor ? 3 : 2);
        this.spawnAdds(body, Math.min(wave, cap - body.adds));
        body.skillCd = body.phase2 ? 2.4 : 3.6;
      }
      return;
    }
    if (body.affix !== "rusher") return;
    if (body.chargeT > 0) return;
    if (body.chargeWind > 0) {
      body.chargeWind -= dt;
      body.vx *= Math.max(0, 1 - dt * 4);
      body.vy *= Math.max(0, 1 - dt * 4);
      if (body.chargeWind <= 0) {
        const len = Math.hypot(body.chargeDirX, body.chargeDirY) || 1;
        body.chargeDirX /= len;
        body.chargeDirY /= len;
        body.chargeT = body.phase2 && this.gateBossFloor ? 0.55 : 0.38;
        // Rampage phase 2: queue a follow-up charge shortly after.
        if (body.phase2 && this.gateBossFloor) body.skillCd = 0.65;
        else body.skillCd = 2.8;
      }
      return;
    }
    body.skillCd -= dt;
    if (body.skillCd > 0 || dist > 260 || dist < 28) return;
    const len = Math.hypot(dx, dy) || 1;
    body.chargeDirX = dx / len;
    body.chargeDirY = dy / len * 0.35;
    body.chargeWind = 0.45;
  }

  private shove(body: Body, power: number, lift: number, stun: number, shake: number, dmg = 0) {
    const dx = this.host.x - body.x;
    const dy = this.host.y - 24 - body.y;
    const dist = Math.hypot(dx, dy) || 1;
    const awayY = dmg > 0 ? (dy / dist) * 220 : 0;
    this.host.knock((dx / dist) * power, lift + awayY);
    this.stun = stun;
    // Journey hits leave a longer blink window and shove the attacker back.
    this.grace = dmg > 0 ? 1.2 : 0.85;
    if (dmg > 0) body.shy = 0.6;
    body.vx = (-dx / dist) * (dmg > 0 ? 280 : 180);
    body.vy = dmg > 0 ? -160 : -80;
    if (dmg > 0) {
      if (this.host.sting?.(dmg)) {
        this.host.shake(Math.max(shake, 0.46));
        this.spawnNum(this.host.x, this.host.y - 48, dmg, false, "#ff6a78", undefined, true);
      }
      return;
    }
    this.host.shake(shake);
    this.host.sfx("hitChunk");
  }

  private damageBody(body: Body, dmg: number, dirX = 0, dirY = -1, punch = false) {
    if (dmg <= 0 || body.hp <= 0) return;
    let deal = dmg;
    if (body.shieldT > 0) deal *= 0.4;
    if (body.affix === "summoner" && body.adds > 0) deal *= 0.7;
    if (body.phase2 && body.affix === "tank" && body.gate) deal *= 0.75;
    deal = Math.max(0.5, deal);
    body.hp -= deal;
    body.flash = punch ? 0.16 : 0.09;
    body.bar = 1.7;
    // Rusher charge is interruptible during telegraph.
    if (body.affix === "rusher" && body.chargeWind > 0) {
      body.chargeWind = 0;
      body.shy = Math.max(body.shy, 0.35);
      body.vx *= 0.2;
    }
    if (!body.phase2 && body.gate && body.hp <= body.maxHp * 0.5) this.enterPhase2(body);
    if (punch) this.hitSparks(body);
    this.spawnNum(body.x, body.y - Math.max(12, body.h * 0.46), Math.max(1, Math.round(deal)), false, "#fff6d8", undefined, true);
    if (body.hit <= 0) {
      body.hit = 0.08;
      this.host.sfx("hitMinion");
      if (body.phase === "chase" || body.phase === "land" || body.phase === "roar") {
        const len = Math.hypot(dirX, dirY) || 1;
        const heavy = body.kind === "boss" || body.gate;
        const kick = Math.min(heavy ? BOSS_KNOCK : MINION_KNOCK, (heavy ? BOSS_KNOCK : MINION_KNOCK) * Math.min(1, deal / 14));
        body.vx += (dirX / len) * kick;
        body.vy += (dirY / len) * kick * 0.45 - 26;
      }
    }
    if (body.hp <= 0) this.kill(body);
  }

  private enterPhase2(body: Body) {
    body.phase2 = true;
    body.flash = 0.28;
    this.host.sfx("roar");
    this.host.shake(0.28);
    this.bursts.push({ x: body.x, y: body.y, life: 0.42, max: 0.42, boss: true });
    if (body.affix === "tank") {
      body.shieldT = body.gate && this.gateBossFloor ? 999 : 3.2;
      body.slam = Math.min(body.slam, 0.4);
    } else if (body.affix === "summoner") {
      body.skillCd = 0.2;
      this.spawnAdds(body, this.gateBossFloor ? 4 : 3);
    } else if (body.affix === "rusher") {
      body.skillCd = 0.4;
    }
  }

  private hitSparks(body: Body) {
    const n = body.kind === "boss" || body.gate ? 7 : 5;
    for (let i = 0; i < n; i++) {
      const ang = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const speed = 70 + Math.random() * 150;
      this.host.parts.chip(
        body.x,
        body.y,
        Math.cos(ang) * speed,
        Math.sin(ang) * speed - 40,
        i % 2 ? hex("#ffffff") : hex("#fff6d8"),
        0.2,
      );
    }
  }

  private markBorn(body: Body) {
    if (body.born) return;
    body.born = true;
    this.spawnedMonsters++;
  }

  private noteDead(body: Body) {
    if (!body.born || body.countedDead) return;
    body.countedDead = true;
    this.slainMonsters++;
  }

  private livingMonsters(): number {
    let n = 0;
    for (const body of this.bodies) if (body.born && !body.countedDead && body.hp > 0) n++;
    const caught = (bit: MagnetBit) => !!bit.body && bit.body.born && !bit.body.countedDead && bit.body.hp > 0;
    for (const bit of this.bits) if (caught(bit)) n++;
    for (const toss of this.tosses) for (const bit of toss.bits) if (caught(bit)) n++;
    for (const body of this.thrown) if (body.born && !body.countedDead && body.hp > 0) n++;
    return n;
  }

  private kill(body: Body) {
    this.noteDead(body);
    const at = this.bodies.indexOf(body);
    if (at >= 0) this.bodies.splice(at, 1);
    if (body.gate) {
      if (this.gateBossFloor) this.drop(body.x, body.y, GATE_BOSS_KILL_COINS, true, GATE_BOSS_GEM);
      else this.drop(body.x, body.y, ELITE_KILL_COINS, true, BOSS_GEM);
      this.gateClearedFlag = true;
      this.gateBody = null;
    } else if (body.kind === "boss") this.drop(body.x, body.y, BOSS_KILL_COINS, true, BOSS_GEM);
    else if (body.purse > 0) this.drop(body.x, body.y, MINION_COINS, false);
    // Notify summoner owners that an add died.
    if (!body.gate && body.kind === "crawl") {
      for (const other of this.bodies) {
        if (other.affix === "summoner" && other.adds > 0) other.adds = Math.max(0, other.adds - 1);
      }
    }
    this.maybeHeart(body.x, body.y, body.gate || body.kind === "boss" ? 0.55 : 0.22);
    this.burst(body);
    this.host.sfx("minionDie");
    this.host.shake(body.gate || body.kind === "boss" ? 0.32 : 0.08);
  }

  private burst(body: Body) {
    const boss = body.kind === "boss" || body.gate;
    this.bursts.push({ x: body.x, y: body.y, life: boss ? 0.36 : 0.28, max: boss ? 0.36 : 0.28, boss });
    const n = boss ? 16 : 9;
    for (let i = 0; i < n; i++) {
      const ang = (i / n) * Math.PI * 2;
      const speed = (boss ? 220 : 150) + Math.random() * 120;
      this.host.parts.chip(body.x, body.y, Math.cos(ang) * speed, Math.sin(ang) * speed - 70, i % 2 ? hex("#fff6d8") : hex("#ffd84d"), 0.48);
    }
    for (let i = 0; i < (boss ? 8 : 4); i++) {
      this.host.parts.chip(body.x, body.y, (Math.random() - 0.5) * 260, -80 - Math.random() * 180, hex("#ffffff"), 0.36);
    }
  }

  private fromSlab(slab: Slab, kind: Body["kind"], purse: number): Body {
    return this.embody({
      x: slab.x, y: slab.y, vx: slab.vx, vy: slab.vy, a: slab.a, av: slab.av,
      w: slab.bw, h: slab.bh,
      canvas: slab.canvas, bx: slab.bx, by: slab.by, bw: slab.bw, bh: slab.bh,
    }, kind, purse, kind === "boss" ? BOSS_HP : MINION_HP);
  }

  private fallback(x: number, y: number, w: number, h: number, kind: Body["kind"], purse: number): Body {
    const bw = Math.max(28, Math.min(120, w));
    const bh = Math.max(28, Math.min(80, h));
    return this.embody({
      x, y, vx: 0, vy: -40, a: 0, av: 0,
      w: bw, h: bh,
      canvas: boxCanvas(bw, bh, "#ff7a2e"),
      bx: -bw / 2, by: -bh / 2, bw, bh,
    }, kind, purse, kind === "boss" ? BOSS_HP : MINION_HP);
  }

  private embody(
    base: Pick<Body, "x" | "y" | "vx" | "vy" | "a" | "av" | "w" | "h" | "canvas" | "bx" | "by" | "bw" | "bh">,
    kind: Body["kind"],
    purse: number,
    hp: number,
  ): Body {
    return {
      ...base,
      hp, maxHp: hp, kind, eyes: kind !== "thrown",
      hop: 0.2, slam: kind === "boss" ? 1.4 : 1, wind: 0, shy: 0, flash: 0, age: 0, purse, hit: 0,
      phase: "chase", phaseT: 0, tele: kind === "boss" ? 1 : 0.8,
      bar: 0, sx: 1, sy: 1, blink: 0.6 + Math.random() * 2.4, shut: 0,
      homeX: base.x, homeY: base.y, homeW: base.w, homeH: base.h,
      title: kind === "boss" ? "BOSS" : "",
      launched: false, dug: false, shoveT: 0,
      born: false,
      countedDead: false,
      affix: "",
      gate: false,
      phase2: false,
      shieldT: 0,
      skillCd: 0,
      chargeWind: 0,
      chargeDirX: 0,
      chargeDirY: 0,
      chargeT: 0,
      adds: 0,
    };
  }

  private enlarge(body: Body, mul: number) {
    const m = Math.max(1, mul);
    body.bw *= m;
    body.bh *= m;
    body.bx *= m;
    body.by *= m;
    body.w = Math.abs(body.bw);
    body.h = Math.abs(body.bh);
    body.homeW = Math.max(body.homeW, body.w);
    body.homeH = Math.max(body.homeH, body.h);
  }

  private gateHp(affix: GateAffix, boss: boolean): number {
    if (boss) {
      if (affix === "tank") return GATE_BOSS_TANK_HP;
      if (affix === "summoner") return GATE_BOSS_SUMMONER_HP;
      return GATE_BOSS_RUSHER_HP;
    }
    if (affix === "tank") return ELITE_TANK_HP;
    if (affix === "summoner") return ELITE_SUMMONER_HP;
    return ELITE_RUSHER_HP;
  }

  private spawnGateFoe(): boolean {
    const world = this.host.world;
    const picks: number[] = [];
    for (let i = 0; i < world.blocks.length; i++) {
      const block = world.blocks[i];
      if (!block?.alive || !block.canDetach || block.children.length > 0) continue;
      if (i === this.bossIndex || this.stirs.some((stir) => stir.index === i)) continue;
      const area = block.w * block.h;
      if (area < 900 || area > 22000 || block.w < 18 || block.h < 18) continue;
      picks.push(i);
    }
    const affix = this.gateAffix;
    const boss = this.gateBossFloor;
    const purse = boss ? GATE_BOSS_KILL_COINS : ELITE_KILL_COINS;
    let body: Body;
    let homeW = 64;
    let homeH = 48;
    if (picks.length) {
      // Prefer a mid-page pick near the player when possible.
      picks.sort((a, b) => {
        const ba = world.blocks[a];
        const bb = world.blocks[b];
        const da = Math.hypot(ba.x + ba.w / 2 - this.host.x, ba.y + ba.h / 2 - this.host.y);
        const db = Math.hypot(bb.x + bb.w / 2 - this.host.x, bb.y + bb.h / 2 - this.host.y);
        return da - db;
      });
      const index = picks[Math.min(picks.length - 1, Math.floor(Math.random() * Math.min(5, picks.length)))];
      const block = world.blocks[index];
      homeW = block.w;
      homeH = block.h;
      this.releaseLoot(index, block.x + block.w / 2, block.y + block.h / 2);
      const slab = this.lift(index);
      body = slab
        ? this.fromSlab(slab, boss || affix === "tank" ? "boss" : affix === "rusher" ? "hop" : "hop", purse)
        : this.fallback(block.x + block.w / 2, block.y + block.h / 2, block.w, block.h, boss ? "boss" : "hop", purse);
      body.x = block.x + block.w / 2;
      body.y = block.y + block.h / 2;
      this.scar(block.x, block.y, block.w, block.h);
    } else {
      const x = Math.max(40, Math.min(world.W - 40, this.host.x + (Math.random() < 0.5 ? -120 : 120)));
      const y = Math.max(40, this.host.y - 80);
      body = this.fallback(x, y, 64, 48, boss ? "boss" : "hop", purse);
    }
    // Tank line uses boss kind so slam AI and bar styling apply; summoner/rusher stay hop with affix AI.
    if (boss || affix === "tank") body.kind = "boss";
    const hp = this.gateHp(affix, boss) * this.toughness();
    body.hp = hp;
    body.maxHp = hp;
    body.eyes = true;
    body.affix = affix;
    body.gate = true;
    body.title = gateTitle(affix, boss);
    body.phase = "telegraph";
    body.phaseT = 0;
    body.tele = boss ? 1.15 : 0.9;
    body.slam = affix === "tank" ? (boss ? 1.6 : 1.35) : 1;
    body.skillCd = affix === "summoner" ? 1.2 : affix === "rusher" ? 1.6 : 0;
    this.enlarge(body, boss ? 2.0 : affix === "tank" ? 1.85 : affix === "summoner" ? 1.55 : 1.45);
    this.fit(body, boss ? 210 : 170);
    body.homeX = body.x;
    body.homeY = body.y;
    body.homeW = Math.max(body.w, homeW);
    body.homeH = Math.max(body.h, homeH);
    this.bodies.push(body);
    this.markBorn(body);
    this.gateBody = body;
    this.host.sfx("roar");
    this.host.shake(boss ? 0.36 : 0.22);
    this.bursts.push({ x: body.x, y: body.y, life: boss ? 0.5 : 0.38, max: boss ? 0.5 : 0.38, boss: true });
    return true;
  }

  private spawnAdds(owner: Body, count: number) {
    const n = Math.max(1, count);
    for (let i = 0; i < n; i++) {
      const ang = (i / n) * Math.PI * 2 + Math.random() * 0.4;
      const x = owner.x + Math.cos(ang) * (36 + Math.random() * 28);
      const y = owner.y + Math.sin(ang) * 18;
      const add = this.fallback(x, y, 28, 22, "crawl", 0);
      add.hp = ADD_HP * this.toughness();
      add.maxHp = add.hp;
      add.eyes = true;
      add.phase = "chase";
      add.vx = Math.cos(ang) * 80;
      add.vy = -40;
      this.bodies.push(add);
      this.markBorn(add);
      owner.adds++;
    }
    this.host.sfx("spawn");
  }

  private fit(body: Body, max: number) {
    const scale = Math.min(1, max / Math.max(body.bw, body.bh, 1));
    if (scale >= 1) return;
    body.bw *= scale;
    body.bh *= scale;
    body.bx *= scale;
    body.by *= scale;
    body.w = body.bw;
    body.h = body.bh;
  }

  private settle(body: Body) {
    const slab: Slab = {
      x: body.x, y: body.y, a: body.a, vx: body.vx * 0.2, vy: Math.min(0, body.vy), av: body.av * 0.2,
      poly: [-body.bw / 2, -body.bh / 2, body.bw / 2, -body.bh / 2, body.bw / 2, body.bh / 2, -body.bw / 2, body.bh / 2],
      canvas: body.canvas, bx: body.bx, by: body.by, bw: body.bw, bh: body.bh,
      colors: new Uint32Array([hex("#c9c9d1")]),
      ccw: 1, cch: 1,
      m: Math.max(40, body.bw * body.bh),
      I: Math.max(80, body.bw * body.bh * 8),
      rest: 0.2,
      fragment: body.bw * body.bh < 900,
      age: 0, impacts: 0, hitFlash: 0, ghost: false,
    };
    this.host.chunks.list.push(slab);
  }

  private stepJuice(dt: number) {
    for (let i = this.floaters.length - 1; i >= 0; i--) {
      const num = this.floaters[i];
      num.life -= dt;
      num.y += num.vy * dt;
      if (num.life <= 0) this.floaters.splice(i, 1);
    }
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      this.bursts[i].life -= dt;
      if (this.bursts[i].life <= 0) this.bursts.splice(i, 1);
    }
    for (let i = this.scars.length - 1; i >= 0; i--) {
      this.scars[i].life -= dt;
      if (this.scars[i].life <= 0) this.scars.splice(i, 1);
    }
    if (this.floaters.length > 40) this.floaters.splice(0, this.floaters.length - 40);
  }

  private stepStirs(dt: number) {
    for (let i = this.stirs.length - 1; i >= 0; i--) {
      const stir = this.stirs[i];
      const block = this.host.world.blocks[stir.index];
      if (!block?.alive) {
        this.stirs.splice(i, 1);
        continue;
      }
      stir.x = block.x;
      stir.y = block.y;
      stir.w = block.w;
      stir.h = block.h;
      stir.t += dt;
      stir.dust -= dt;
      if (stir.dust <= 0) {
        stir.dust = 0.08;
        this.dust(stir.x, stir.y, stir.w, stir.h, false);
      }
      if (stir.cracked < 3 && stir.t > (stir.cracked + 1) * stir.dur * 0.28) {
        stir.cracked++;
        this.host.world.crack(
          stir.index,
          stir.x + stir.w * (0.2 + Math.random() * 0.6),
          stir.y + stir.h * (0.2 + Math.random() * 0.6),
        );
      }
      if (stir.t < stir.dur) continue;
      this.stirs.splice(i, 1);
      this.peelStir(stir);
    }
  }

  private peelStir(stir: Stir): Body | null {
    const slab = this.lift(stir.index);
    if (!slab) return null;
    this.releaseLoot(stir.index, stir.x + stir.w / 2, stir.y + stir.h / 2);
    const body = this.fromSlab(slab, stir.kind, stir.purse);
    const hp = MINION_HP * this.toughness();
    body.hp = hp;
    body.maxHp = hp;
    body.eyes = true;
    this.fit(body, 96);
    body.x = stir.x + stir.w / 2;
    body.y = stir.y + stir.h / 2;
    body.vx = 0;
    body.vy = 0;
    body.homeX = body.x;
    body.homeY = body.y;
    body.homeW = stir.w;
    body.homeH = stir.h;
    body.phase = "peel";
    body.phaseT = 0;
    this.bodies.push(body);
    this.markBorn(body);
    this.scar(stir.x, stir.y, stir.w, stir.h);
    this.host.sfx("hitBlock");
    return body;
  }

  private pickBody(x0: number, y0: number, x1: number, y1: number, pad = 0): { t: number; index: number } {
    let t = -1;
    let index = -1;
    for (let i = 0; i < this.bodies.length; i++) {
      const hit = this.segBody(x0, y0, x1, y1, this.bodies[i], pad);
      if (hit >= 0 && (t < 0 || hit < t)) {
        t = hit;
        index = i;
      }
    }
    return { t, index };
  }

  /** Peel the waking element the segment reaches first, if it is not behind a minion. */
  private peelFirstStir(x0: number, y0: number, x1: number, y1: number, limit: number): number {
    let best = -1;
    let who = -1;
    for (let i = 0; i < this.stirs.length; i++) {
      const stir = this.stirs[i];
      const t = segBox(x0, y0, x1, y1, stir.x, stir.y, stir.x + stir.w, stir.y + stir.h);
      if (t < 0 || t > limit) continue;
      if (best < 0 || t < best) {
        best = t;
        who = i;
      }
    }
    if (who < 0) return -1;
    if (!this.peelStir(this.stirs[who])) return -1;
    this.stirs.splice(who, 1);
    return best;
  }

  private peelInRadius(x: number, y: number, r: number) {
    for (let i = this.stirs.length - 1; i >= 0; i--) {
      const stir = this.stirs[i];
      if (this.rectDist(stir.x, stir.y, stir.w, stir.h, x, y) > r) continue;
      if (!this.peelStir(stir)) continue;
      this.stirs.splice(i, 1);
    }
  }

  private segBody(x0: number, y0: number, x1: number, y1: number, body: Body, pad = 0): number {
    const [l, t, r, b] = this.hitBox(body);
    return segBox(x0, y0, x1, y1, l - pad, t - pad, r + pad, b + pad);
  }

  private nearDist(body: Body, x: number, y: number): number {
    const [l, t, r, b] = this.hitBox(body);
    const cx = Math.max(l, Math.min(x, r));
    const cy = Math.max(t, Math.min(y, b));
    return Math.hypot(x - cx, y - cy);
  }

  /** Logical hurt box union the drawn sprite, so a hit on visible pixels counts as contact. */
  private hitBox(body: Body): [number, number, number, number] {
    const core = this.bodyBox(body);
    const sprite = this.spriteBox(body);
    return [
      Math.min(core[0], sprite[0]),
      Math.min(core[1], sprite[1]),
      Math.max(core[2], sprite[2]),
      Math.max(core[3], sprite[3]),
    ];
  }

  private spriteBox(body: Body): [number, number, number, number] {
    const cos = Math.cos(body.a);
    const sin = Math.sin(body.a);
    const sx = body.sx || 1;
    const sy = body.sy || 1;
    const corners: [number, number][] = [
      [body.bx, body.by],
      [body.bx + body.bw, body.by],
      [body.bx, body.by + body.bh],
      [body.bx + body.bw, body.by + body.bh],
    ];
    let l = Infinity;
    let t = Infinity;
    let r = -Infinity;
    let b = -Infinity;
    for (const [lx, ly] of corners) {
      const x = body.x + lx * sx * cos - ly * sy * sin;
      const y = body.y + lx * sx * sin + ly * sy * cos;
      if (x < l) l = x;
      if (y < t) t = y;
      if (x > r) r = x;
      if (y > b) b = y;
    }
    const pad = 2;
    return [l - pad, t - pad, r + pad, b + pad];
  }

  private bodyBox(body: Body): [number, number, number, number] {
    const wide = body.phase === "chase" ? body.w : Math.max(body.w, body.homeW);
    const tall = body.phase === "chase" ? body.h : Math.max(body.h, body.homeH);
    const c = Math.abs(Math.cos(body.a));
    const s = Math.abs(Math.sin(body.a));
    const hw = wide * 0.5;
    const hh = tall * 0.5;
    const rw = hw * c + hh * s;
    const rh = hw * s + hh * c;
    return [body.x - rw, body.y - rh, body.x + rw, body.y + rh];
  }

  private boxDist(body: Body, x: number, y: number): number {
    const [l, t, r, b] = this.bodyBox(body);
    const cx = Math.max(l, Math.min(x, r));
    const cy = Math.max(t, Math.min(y, b));
    return Math.hypot(x - cx, y - cy);
  }

  private rectDist(x: number, y: number, w: number, h: number, px: number, py: number): number {
    const cx = Math.max(x, Math.min(px, x + w));
    const cy = Math.max(y, Math.min(py, y + h));
    return Math.hypot(px - cx, py - cy);
  }

  private stepAwake(body: Body, dt: number) {
    body.phaseT += dt;
    const boss = body.kind === "boss";
    if (body.phase === "telegraph") {
      const j = (boss ? 2 : 1) * SCALE;
      body.x = body.homeX + (Math.sin(this.t * 62) > 0 ? j : -j);
      body.y = body.homeY + (Math.cos(this.t * 48) > 0 ? j : -j);
      body.vx = 0;
      body.vy = 0;
      body.a = Math.sin(this.t * 40) * (boss ? 0.08 : 0.04);
      this.puffTick(body, dt);
      if (body.phaseT >= body.tele) this.enter(body, "peel");
      return;
    }
    if (body.phase === "peel") {
      const dur = boss ? 0.66 : 0.46;
      const u = Math.min(1, body.phaseT / dur);
      const side = Math.sign(this.focus.x - body.homeX) || 1;
      const pull = boss ? 36 : 16;
      const wob = u < 0.55 ? Math.round(Math.sin(this.t * 58)) * SCALE : 0;
      body.x = body.homeX + side * pull * u * u + wob;
      body.y = body.homeY - Math.sin(u * Math.PI) * (boss ? 26 : 14) - pull * 0.35 * u;
      body.a = side * 0.42 * Math.sin(u * Math.PI);
      body.sx = 1 + Math.sin(u * Math.PI) * (boss ? 0.3 : 0.18);
      body.sy = 1 - Math.sin(u * Math.PI) * 0.1 + u * (boss ? 0.2 : 0.14);
      body.vx = side * (boss ? 110 : 70);
      body.vy = boss ? 140 : 90;
      this.puffTick(body, dt);
      if (u >= 1) {
        body.a = 0;
        body.phase = "land";
        body.phaseT = 0;
        body.vy = body.kind === "fly" ? -30 : body.vy;
      }
      return;
    }
    if (body.phase === "land") {
      this.awakeFall(body, dt, boss);
      return;
    }
    body.sx += (1 - body.sx) * Math.min(1, dt * 7);
    body.sy += (1 - body.sy) * Math.min(1, dt * 7);
    body.vx *= Math.max(0, 1 - dt * 3);
    if (body.kind !== "fly") body.vy = Math.min(900, body.vy + 1400 * dt);
    body.x += body.vx * dt;
    body.y += body.vy * dt;
    const grounded = this.host.world.matAt(body.x, body.y + body.h * 0.45 + 2) >= SOLID && body.vy >= 0;
    if (grounded && body.kind !== "fly") {
      body.vy = 0;
      body.y -= 1;
    }
    body.x = Math.max(10, Math.min(this.host.world.W - 10, body.x));
    if (body.y > this.host.world.H + 48) {
      this.kill(body);
      return;
    }
    if (body.phaseT >= (boss ? 0.8 : 0.7)) {
      body.phase = "chase";
      body.phaseT = 0;
      body.sx = 1;
      body.sy = 1;
      body.a = 0;
    }
  }

  private awakeFall(body: Body, dt: number, boss: boolean) {
    if (body.kind === "fly") {
      const u = Math.min(1, body.phaseT / 0.22);
      body.sy = 1 + Math.sin(u * Math.PI) * (boss ? 0.28 : 0.18);
      body.sx = 1 - Math.sin(u * Math.PI) * 0.08;
      body.y += Math.sin(this.t * 6) * 12 * dt;
      if (body.phaseT > 0.26) this.enter(body, "roar");
      return;
    }
    body.vy = Math.min(980, body.vy + 1600 * dt);
    body.x += body.vx * dt;
    body.y += body.vy * dt;
    body.x = Math.max(10, Math.min(this.host.world.W - 10, body.x));
    const grounded = this.host.world.matAt(body.x, body.y + body.h * 0.45 + 3) >= SOLID && body.vy >= 0;
    if (grounded) {
      body.vy = 0;
      this.enter(body, "roar");
      return;
    }
    if (body.y > this.host.world.H + 48) {
      this.kill(body);
      return;
    }
    if (body.phaseT > 1.15) this.enter(body, "roar");
  }

  private enter(body: Body, phase: Body["phase"]) {
    body.phase = phase;
    body.phaseT = 0;
    if (phase === "peel") this.host.sfx(body.kind === "boss" ? "shatter" : "hitBlock");
    if (phase === "roar") {
      const boss = body.kind === "boss";
      body.sx = boss ? 1.42 : 1.28;
      body.sy = boss ? 0.62 : 0.7;
      this.host.sfx("roar");
      this.host.shake(boss ? 0.3 : 0.12);
    }
  }

  private puffTick(body: Body, dt: number) {
    const step = Math.floor(body.phaseT * 12);
    const prev = Math.floor((body.phaseT - dt) * 12);
    if (step === prev) return;
    this.dust(body.x - body.homeW / 2, body.y - body.homeH / 2, body.homeW, body.homeH, body.kind === "boss");
  }

  private dust(x: number, y: number, w: number, h: number, boss: boolean) {
    const n = boss ? 3 : 2;
    for (let i = 0; i < n; i++) {
      this.host.parts.smoke(
        x + Math.random() * w, y + Math.random() * h,
        (Math.random() - 0.5) * (boss ? 70 : 46),
        -24 - Math.random() * (boss ? 50 : 30),
        (boss ? 1.8 : 1.2) * CELL,
        0.32 + Math.random() * 0.22,
        "#6a6272",
      );
    }
    this.host.parts.chip(
      x + Math.random() * w, y + Math.random() * h,
      (Math.random() - 0.5) * 90, -30 - Math.random() * 70,
      hex("#d7d0c2"),
      0.36,
    );
  }

  private scar(x: number, y: number, w: number, h: number) {
    this.scars.push({ x, y, w, h, life: 2.8, max: 2.8 });
    if (this.scars.length > 16) this.scars.splice(0, this.scars.length - 16);
  }

  private tickBlink(body: Body, dt: number) {
    if (body.phase === "roar") {
      body.shut = 0;
      return;
    }
    if (body.shut > 0) {
      body.shut -= dt;
      return;
    }
    body.blink -= dt;
    if (body.blink > 0) return;
    body.blink = 2.2 + Math.random() * 3.2;
    body.shut = 0.1 + Math.random() * 0.04;
  }

  private spawnNum(x: number, y: number, n: number, plus = false, color = "#fff6d8", icon?: "coin" | "gem", big = false) {
    const last = this.floaters[this.floaters.length - 1];
    if (last && last.plus === plus && last.color === color && last.icon === icon && last.big === big && last.life > 0.55 && Math.hypot(last.x - x, last.y - y) < 36) {
      last.n += n;
      last.life = plus ? 0.9 : 0.7;
      last.y = Math.min(last.y, y);
      return;
    }
    this.floaters.push({ x, y, vy: plus ? -28 : -42, life: plus ? 0.9 : 0.7, n, plus, color, icon, big });
  }

  private drawBody(ctx: CanvasRenderingContext2D, body: Body) {
    if (body.affix && body.gate && body.phase === "chase") this.drawAffixAura(ctx, body);
    if (body.affix === "rusher" && body.chargeWind > 0) this.drawChargeLine(ctx, body);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.translate(Math.round(body.x), Math.round(body.y));
    ctx.rotate(body.a);
    ctx.scale(body.sx, body.sy);
    ctx.drawImage(body.canvas, body.bx, body.by, body.bw, body.bh);
    if (body.flash > 0) this.paintFlash(ctx, body);
    if (body.shieldT > 0) {
      ctx.strokeStyle = "rgba(120, 200, 255, 0.85)";
      ctx.lineWidth = 3;
      ctx.strokeRect(body.bx - 3, body.by - 3, body.bw + 6, body.bh + 6);
    }
    if (body.phase === "telegraph" || body.phase === "peel") this.drawWakeCracks(ctx, body);
    if (body.eyes) this.paintFace(ctx, body);
    ctx.restore();
    if (body.bar > 0 && body.hp < body.maxHp - 0.4 && body.kind !== "boss" && !body.gate) {
      const alpha = body.bar > 0.4 ? 1 : body.bar / 0.4;
      const width = Math.max(SCALE * 6, Math.round(Math.min(body.w, 48) / SCALE) * SCALE);
      drawBar(ctx, body.x, body.y - body.h * 0.5 - SCALE * 3, width, SCALE * 2, body.hp / body.maxHp, alpha);
    }
    if (body.wind > 0 && body.phase === "chase") {
      ctx.strokeStyle = "rgba(255, 90, 70, 0.85)";
      ctx.strokeRect(body.x - body.w * 0.5, body.y - body.h * 0.5, body.w, body.h);
    }
  }

  private drawAffixAura(ctx: CanvasRenderingContext2D, body: Body) {
    const pad = body.gate && this.gateBossFloor ? 14 : 10;
    const color = body.affix === "tank"
      ? "rgba(255, 196, 72, 0.55)"
      : body.affix === "summoner"
        ? "rgba(180, 110, 255, 0.55)"
        : "rgba(255, 90, 90, 0.55)";
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = body.phase2 ? 4 : 3;
    ctx.globalAlpha = 0.7 + Math.sin(this.t * 6) * 0.2;
    ctx.strokeRect(
      Math.round(body.x - body.w * 0.5 - pad),
      Math.round(body.y - body.h * 0.5 - pad),
      Math.round(body.w + pad * 2),
      Math.round(body.h + pad * 2),
    );
    ctx.restore();
  }

  private drawChargeLine(ctx: CanvasRenderingContext2D, body: Body) {
    const len = 140 + (body.phase2 ? 40 : 0);
    const x1 = body.x + body.chargeDirX * len;
    const y1 = body.y + body.chargeDirY * len;
    ctx.save();
    ctx.strokeStyle = "rgba(255, 70, 70, 0.9)";
    ctx.lineWidth = 3;
    ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(body.x, body.y);
    ctx.lineTo(x1, y1);
    ctx.stroke();
    ctx.restore();
  }

  private paintFlash(ctx: CanvasRenderingContext2D, body: Body) {
    const w = Math.max(1, Math.ceil(Math.abs(body.bw)));
    const h = Math.max(1, Math.ceil(Math.abs(body.bh)));
    let stamp = this.stamp;
    if (!stamp || stamp.width < w || stamp.height < h) {
      stamp = document.createElement("canvas");
      stamp.width = Math.max(w, stamp?.width ?? 0, 8);
      stamp.height = Math.max(h, stamp?.height ?? 0, 8);
      this.stamp = stamp;
    }
    const g = stamp.getContext("2d");
    if (!g) return;
    g.imageSmoothingEnabled = false;
    g.clearRect(0, 0, w, h);
    g.globalCompositeOperation = "source-over";
    g.drawImage(body.canvas, 0, 0, w, h);
    g.globalCompositeOperation = "source-in";
    g.fillStyle = "#ffffff";
    g.fillRect(0, 0, w, h);
    g.globalCompositeOperation = "source-over";
    ctx.save();
    ctx.globalAlpha = Math.min(1, body.flash / 0.045);
    ctx.drawImage(stamp, 0, 0, w, h, body.bx, body.by, body.bw, body.bh);
    ctx.restore();
  }

  private paintFace(ctx: CanvasRenderingContext2D, body: Body) {
    const cx = body.bx + body.bw / 2;
    const cy = body.by + body.bh / 2;
    const dx = this.focus.x - body.x;
    const dy = this.focus.y - body.y;
    const cos = Math.cos(body.a);
    const sin = Math.sin(body.a);
    const lx = dx * cos + dy * sin;
    const ly = -dx * sin + dy * cos;
    const pop = body.phase === "telegraph" ? Math.max(0, Math.min(1, (body.phaseT - 0.16) / 0.34)) : 1;
    drawMonsterFace(ctx, cx, cy, body.bw, body.bh, cx + lx, cy + ly, this.mouthOf(body), body.shut > 0, pop, body.kind === "boss");
  }

  private mouthOf(body: Body): number {
    if (body.phase === "roar") return 1;
    if (body.phase === "telegraph") {
      const u = body.tele > 0 ? body.phaseT / body.tele : 1;
      return u > 0.62 ? 0.28 + Math.abs(Math.sin(this.t * 14)) * 0.4 : 0;
    }
    if (body.phase === "peel") return 0.4 + Math.abs(Math.sin(this.t * 16)) * 0.5;
    if (body.phase === "land") return 0.25 + Math.abs(Math.sin(this.t * 12)) * 0.3;
    const speed = Math.hypot(body.vx, body.vy);
    const dist = Math.hypot(this.focus.x - body.x, this.focus.y - body.y);
    if (body.wind > 0 || dist < 90) return 0.7 + Math.abs(Math.sin(this.t * 20)) * 0.3;
    if (speed > 40) return 0.2 + Math.abs(Math.sin(this.t * 11 + body.x * 0.05)) * 0.65;
    return 0.08;
  }

  private drawWakeCracks(ctx: CanvasRenderingContext2D, body: Body) {
    const u = body.phase === "telegraph" ? body.phaseT / Math.max(0.1, body.tele) : 1;
    const n = Math.min(body.kind === "boss" ? 6 : 4, 1 + Math.floor(u * 5));
    const unit = SCALE;
    ctx.fillStyle = "#140c18";
    for (let i = 0; i < n; i++) {
      const y = Math.round((body.by + ((i + 1) / (n + 1)) * body.bh) / unit) * unit;
      const len = Math.max(unit * 2, Math.round(body.bw * (0.28 + (i % 3) * 0.12) / unit) * unit);
      const x = Math.round((body.bx + (i % 2) * body.bw * 0.12) / unit) * unit;
      ctx.fillRect(x, y, len, unit);
    }
  }

  private drawStir(ctx: CanvasRenderingContext2D, stir: Stir) {
    const j = (Math.sin(this.t * 61 + stir.index) > 0 ? 1 : -1) * SCALE;
    const k = (Math.cos(this.t * 53 + stir.index) > 0 ? 1 : -1) * SCALE;
    const z = this.host.world.Z;
    const x = Math.round(stir.x);
    const y = Math.round(stir.y);
    const w = Math.round(stir.w);
    const h = Math.round(stir.h);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "rgba(12, 8, 18, 0.28)";
    ctx.fillRect(x, y, w, h);
    this.host.world.pageCache.copy(ctx, stir.x * z, stir.y * z, stir.w * z, stir.h * z, x + j, y + k, w, h);
    this.host.world.textCache.copy(ctx, stir.x * z, stir.y * z, stir.w * z, stir.h * z, x + j, y + k, w, h);
    strokePx(ctx, x + j, y + k, w, h, "#1a1422", SCALE);
    const pop = Math.max(0, Math.min(1, (stir.t - 0.16) / 0.32));
    const open = stir.t > stir.dur * 0.62 ? 0.3 + Math.abs(Math.sin(this.t * 14)) * 0.45 : 0;
    drawMonsterFace(
      ctx, x + w / 2 + j, y + h / 2 + k, w, h,
      this.focus.x, this.focus.y, open, false, pop, false,
    );
    ctx.restore();
  }

  private drawScar(ctx: CanvasRenderingContext2D, scar: Scar) {
    const alpha = Math.max(0, scar.life / scar.max);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(8, 6, 14, 0.45)";
    ctx.fillRect(Math.round(scar.x), Math.round(scar.y), Math.round(scar.w), Math.round(scar.h));
    strokePx(ctx, scar.x, scar.y, scar.w, scar.h, "#1a1422", SCALE);
    ctx.restore();
  }

  private drawBurst(ctx: CanvasRenderingContext2D, burst: Burst) {
    const u = 1 - burst.life / burst.max;
    const rad = (burst.boss ? 16 : 8) + u * (burst.boss ? 48 : 26);
    ctx.save();
    ctx.globalAlpha = 1 - u;
    strokePx(ctx, burst.x - rad, burst.y - rad, rad * 2, rad * 2, "#fff6d8", SCALE);
    ctx.restore();
  }

  private drawLootGlint(ctx: CanvasRenderingContext2D, world: World, id: number, view?: { x: number; y: number; w: number; h: number }) {
    const block = world.blocks[id];
    if (!block?.alive) return;
    if (view) {
      if (block.x + block.w < view.x - 24 || block.x > view.x + view.w + 24) return;
      if (block.y + block.h < view.y - 24 || block.y > view.y + view.h + 24) return;
    }
    const cycle = 4.2;
    const phase = (this.t + id * 0.37) % cycle;
    if (phase > 0.18) return;
    const alpha = Math.sin((phase / 0.18) * Math.PI) * 0.85;
    const gx = block.x + Math.min(block.w - 3, Math.max(3, block.w * 0.68));
    const gy = block.y + Math.min(block.h - 3, Math.max(3, block.h * 0.3));
    drawGlint(ctx, gx, gy, alpha);
  }

  private drawCoin(ctx: CanvasRenderingContext2D, coin: Coin, view?: { x: number; y: number; w: number; h: number }) {
    if (view) {
      if (coin.x < view.x - 24 || coin.x > view.x + view.w + 24) return;
      if (coin.y < view.y - 24 || coin.y > view.y + view.h + 24) return;
    }
    if (coin.life < COIN_BLINK) {
      const hz = coin.life < 1.6 ? 14 : 6;
      if (Math.sin(coin.life * hz) < 0) return;
    }
    const sheet = coinSheet(coin.gem);
    const rate = coin.gem ? 12 : 8;
    const frame = sheet[Math.floor(this.t * rate + coin.spin) % sheet.length];
    const px = Math.round(coin.x);
    const py = Math.round(coin.y);
    ctx.fillStyle = "rgba(26, 20, 34, 0.45)";
    ctx.fillRect(px - 6, py + 6, 12, 2);
    ctx.drawImage(frame, px - frame.width / 2, py - frame.height / 2);
    if (Math.sin(this.t * 9 + coin.spin) > 0.72) {
      ctx.fillStyle = coin.gem ? "#f4e8ff" : "#fff6d8";
      ctx.fillRect(px + 8, py - 9, 2, 2);
    }
  }

  private drawFloater(ctx: CanvasRenderingContext2D, num: Floater) {
    const alpha = num.life > 0.2 ? 1 : num.life / 0.2;
    ctx.save();
    ctx.globalAlpha = alpha;
    const text = (num.plus ? "+" : "") + String(Math.max(0, Math.round(num.n)));
    const color = num.color || "#fff6d8";
    const unit = num.big ? 3 : SCALE;
    let x = num.x - text.length * unit * 2;
    if (num.icon) {
      const frame = coinSheet(num.icon === "gem")[0];
      x -= frame.width * 0.35;
      ctx.drawImage(frame, Math.round(x - frame.width - 2), Math.round(num.y + SCALE * 2 - frame.height / 2));
    }
    if (num.big) drawOutlined(ctx, text, x, num.y, unit, color);
    else {
      drawText(ctx, text, x, num.y, SCALE, "#1a1422");
      drawText(ctx, text, x - SCALE, num.y - SCALE, SCALE, color);
    }
    ctx.restore();
  }

  private drawBossBar(ctx: CanvasRenderingContext2D, boss: Body, view?: { x: number; y: number; w: number; h: number }) {
    const unit = SCALE;
    const frac = Math.max(0, boss.hp / boss.maxHp);
    const label = boss.title || "BOSS";
    const tint = boss.phase2 ? "#ffb0b8" : boss.gate && !this.gateBossFloor ? "#ffe08a" : "#fff6d8";
    if (view) {
      const barW = Math.round(Math.min(view.w * 0.52, 320) / unit) * unit;
      const x = Math.round((view.x + (view.w - barW) / 2) / unit) * unit;
      const y = Math.round((view.y + BOSS_BAR_OFFSET + this.barLift) / unit) * unit;
      const labelW = label.length * 4 * unit - unit;
      drawOutlined(ctx, label, x + (barW - labelW) / 2, y - unit * 7, unit, tint);
      drawBar(ctx, x + barW / 2, y, barW, unit * 3, frac, 1);
      return;
    }
    const width = Math.max(unit * 16, Math.round(Math.min(boss.homeW, 160) / unit) * unit);
    drawOutlined(ctx, label, boss.x - label.length * unit * 2, boss.y - boss.h * 0.5 - unit * 12, unit, tint);
    drawBar(ctx, boss.x, boss.y - boss.h * 0.5 - unit * 4, width, unit * 3, frac, 1);
  }

}

const COIN_PAL: Record<string, string> = {
  o: "#07060a",
  d: "#b8741a",
  m: "#f5a623",
  g: "#ffd23f",
  w: "#ffffff",
  k: "#07060a",
};
const GEM_PAL: Record<string, string> = {
  o: "#1a1422",
  g: "#b98cff",
  h: "#f4e8ff",
  s: "#6c4aad",
  w: "#ffffff",
};
/** Full-face gold coin, 14px. A vertical shine reads clearer than a tiny dollar mark. */
const COIN_FACE = [
  "....oooooo....",
  "..ooddddddoo..",
  ".odddggggdddo.",
  ".odwwgwwggggdo",
  "odggggwwggggdo",
  "odggggwwggggdo",
  "odggggwwggggdo",
  "odmmmmwwmmmmdo",
  "odmmmmwwmmmmdo",
  ".odmmmmmmmmmdo",
  ".odddmmmmddoo.",
  "..oodddddooo..",
  "...oooooooo...",
  "....oooooo....",
];
/** Edge-on frame: a thick gold disc with a dark rim. */
const COIN_EDGE = [
  "..............",
  "......oo......",
  ".....oggo.....",
  "....ogddo.....",
  "....ogmmo.....",
  "...ogmmmo.....",
  "...ogmmmo.....",
  "...ogmmmo.....",
  "...ogmmmo.....",
  "....ogmmo.....",
  "....ogddo.....",
  ".....oggo.....",
  "......oo......",
  "..............",
];

function squashCoin(rows: string[], width: number): string[] {
  const n = rows.length;
  const left = Math.floor((n - width) / 2);
  return rows.map((row) => {
    const out = Array(n).fill(".");
    for (let i = 0; i < width; i++) {
      const sx = Math.min(n - 1, Math.max(0, Math.round((i + 0.5) * n / width - 0.5)));
      out[left + i] = row[sx] || ".";
    }
    return out.join("");
  });
}

function coinFrameRows(): string[][] {
  const narrow = squashCoin(COIN_FACE, 9);
  const back = narrow.map((row) => [...row].reverse().join(""));
  return [COIN_FACE, narrow, COIN_EDGE, back];
}
const GEM_FRAMES = [
  ["..o..", ".oho.", "owgwo", ".oso.", "..o.."],
  ["..o..", ".oh..", ".owg.", "..so.", "..o.."],
  ["..o..", "..h..", "..w..", "..s..", "..o.."],
  ["..o..", "..ho.", ".gwo.", ".os..", "..o.."],
  ["..o..", ".oho.", ".wgwo", ".oso.", "..o.."],
];
const spriteCache = new Map<string, HTMLCanvasElement>();

function pixelFrames(name: string, frames: string[][], palette: Record<string, string>, scale = 2): HTMLCanvasElement[] {
  return frames.map((rows, frame) => {
    const key = `${name}:${frame}`;
    const cached = spriteCache.get(key);
    if (cached) return cached;
    const h = rows.length;
    const w = rows.reduce((m, row) => Math.max(m, row.length), 0);
    const canvas = document.createElement("canvas");
    canvas.width = w * scale;
    canvas.height = h * scale;
    const g = canvas.getContext("2d");
    if (g) {
      g.imageSmoothingEnabled = false;
      for (let y = 0; y < h; y++) {
        const row = rows[y];
        for (let x = 0; x < row.length; x++) {
          const color = palette[row[x]];
          if (!color) continue;
          g.fillStyle = color;
          g.fillRect(x * scale, y * scale, scale, scale);
        }
      }
    }
    spriteCache.set(key, canvas);
    return canvas;
  });
}

let coinSheets: HTMLCanvasElement[] | null = null;
let gemSheets: HTMLCanvasElement[] | null = null;

function coinSheet(gem: boolean): HTMLCanvasElement[] {
  if (gem) {
    gemSheets ??= pixelFrames("gem", GEM_FRAMES, GEM_PAL, 2);
    return gemSheets;
  }
  coinSheets ??= pixelFrames("coin", coinFrameRows(), COIN_PAL, 1);
  return coinSheets;
}

let coinFace: string | null = null;

/** Full-face coin as a data URL, shared by the HUD, prices, and pickup flyers. */
export function coinFaceURL(): string {
  coinFace ??= coinSheet(false)[0].toDataURL();
  return coinFace;
}

function drawGlint(ctx: CanvasRenderingContext2D, x: number, y: number, alpha: number) {
  const px = Math.round(x);
  const py = Math.round(y);
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.fillStyle = "#fff6d8";
  ctx.fillRect(px - 1, py - 1, 2, 2);
  ctx.fillStyle = "#ffd84d";
  ctx.fillRect(px - 5, py - 1, 4, 2);
  ctx.fillRect(px + 1, py - 1, 4, 2);
  ctx.fillRect(px - 1, py - 5, 2, 4);
  ctx.fillRect(px - 1, py + 1, 2, 4);
  ctx.restore();
}

const FACE_COLS = 15;
const FACE_ROWS = 12;
const INK = "#1a1422";
const TOOTH = "#f6f1e4";
const EYE = "#fffef8";
const GUM = "#e0233c";
const GULLET = "#3a0a16";

const GLYPH: Record<string, string> = {
  "0": "111101101101111",
  "1": "110010010010111",
  "2": "111001111100111",
  "3": "111001111001111",
  "4": "101101111001001",
  "5": "111100111001111",
  "6": "111100111101111",
  "7": "111001001001001",
  "8": "111101111101111",
  "9": "111101111001111",
  "+": "000010111010000",
  B: "110101110101110",
  O: "111101101101111",
  S: "111100111001111",
};

function faceUnit(w: number, h: number, boss: boolean): number {
  const minU = SCALE;
  const boxW = Math.max(w, FACE_COLS * minU);
  const boxH = Math.max(h, FACE_ROWS * minU);
  const box = Math.min(boxW, boxH) * (boss ? 0.82 : 0.7);
  let unit = Math.floor(box / FACE_ROWS);
  if (unit >= SCALE) unit = Math.floor(unit / SCALE) * SCALE;
  else unit = minU;
  while (unit > minU && (unit * FACE_COLS > boxW || unit * FACE_ROWS > boxH)) unit -= SCALE;
  const cap = (boss ? 4 : 3) * SCALE;
  if (unit > cap) unit = cap;
  return Math.max(minU, unit);
}

function drawMonsterFace(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  lookX: number, lookY: number,
  open: number, blink: boolean, pop: number, boss: boolean,
) {
  const unit = faceUnit(w, h, boss);
  const extra = Math.max(0, Math.min(3, Math.round(Math.max(0, Math.min(1, open)) * 3)));
  const rows = 8 + extra;
  const left = Math.round(x - (FACE_COLS * unit) / 2);
  const top = Math.round(y - (rows * unit) / 2);
  const ang = Math.atan2(lookY - y, lookX - x);
  const px = Math.max(-1, Math.min(1, Math.round(Math.cos(ang))));
  const py = Math.max(-1, Math.min(1, Math.round(Math.sin(ang))));
  const eyeAt = (col: number) => {
    if (pop <= 0.08) return;
    const ox = left + col * unit;
    const oy = top;
    if (pop < 0.42) {
      ctx.fillStyle = INK;
      ctx.fillRect(ox, oy + unit, 4 * unit, 2 * unit);
      ctx.fillStyle = EYE;
      ctx.fillRect(ox + unit, oy + unit, 2 * unit, unit);
      return;
    }
    ctx.fillStyle = INK;
    ctx.fillRect(ox, oy, 4 * unit, 4 * unit);
    if (blink || pop < 0.75) {
      ctx.fillStyle = EYE;
      ctx.fillRect(ox + unit, oy + 2 * unit, 2 * unit, unit);
      return;
    }
    ctx.fillStyle = EYE;
    ctx.fillRect(ox + unit, oy + unit, 2 * unit, 2 * unit);
    ctx.fillStyle = INK;
    ctx.fillRect(ox + (px > 0 ? 2 : 1) * unit, oy + (py > 0 ? 2 : 1) * unit, unit, unit);
  };
  eyeAt(1);
  eyeAt(10);
  const mx = left + unit;
  const my = top + 5 * unit;
  const mw = 13 * unit;
  const mh = (2 + extra) * unit;
  ctx.fillStyle = INK;
  ctx.fillRect(mx - unit, my - unit, mw + unit * 2, mh + unit * 2);
  ctx.fillStyle = extra > 0 ? GUM : "#6e1c2c";
  ctx.fillRect(mx, my, mw, mh);
  if (extra >= 2) {
    ctx.fillStyle = GULLET;
    ctx.fillRect(mx + unit * 2, my + unit, mw - unit * 4, extra * unit);
  }
  ctx.fillStyle = TOOTH;
  for (let i = 0; i < 6; i++) {
    const tx = mx + unit + i * 2 * unit;
    ctx.fillRect(tx, my, unit, unit);
    ctx.fillRect(tx, my + mh - unit, unit, unit);
  }
}

function drawOutlined(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, unit: number, color: string) {
  for (let oy = -unit; oy <= unit; oy += unit) {
    for (let ox = -unit; ox <= unit; ox += unit) {
      if (ox === 0 && oy === 0) continue;
      drawText(ctx, text, x + ox, y + oy, unit, INK);
    }
  }
  drawText(ctx, text, x, y, unit, color);
}

function drawText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, unit: number, color: string) {
  let pen = Math.round(x);
  const top = Math.round(y);
  ctx.fillStyle = color;
  for (const ch of text) {
    const glyph = GLYPH[ch];
    if (!glyph) {
      pen += unit * 4;
      continue;
    }
    for (let i = 0; i < glyph.length; i++) {
      if (glyph[i] !== "1") continue;
      ctx.fillRect(pen + (i % 3) * unit, top + ((i / 3) | 0) * unit, unit, unit);
    }
    pen += unit * 4;
  }
}

function drawBar(ctx: CanvasRenderingContext2D, cx: number, y: number, w: number, h: number, frac: number, alpha: number) {
  const width = Math.max(SCALE, Math.round(w / SCALE) * SCALE);
  const height = Math.max(SCALE, Math.round(h / SCALE) * SCALE);
  const left = Math.round(cx - width / 2);
  const top = Math.round(y);
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.fillStyle = "#1a1422";
  ctx.fillRect(left - SCALE, top - SCALE, width + SCALE * 2, height + SCALE * 2);
  ctx.fillStyle = "#3a2430";
  ctx.fillRect(left, top, width, height);
  const fill = Math.max(0, Math.min(width, Math.round((width * Math.max(0, Math.min(1, frac))) / SCALE) * SCALE));
  ctx.fillStyle = frac < 0.35 ? "#ff4b3a" : "#7dff4a";
  if (fill > 0) ctx.fillRect(left, top, fill, height);
  ctx.restore();
}

function strokePx(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string, unit: number) {
  const left = Math.round(x);
  const top = Math.round(y);
  const width = Math.round(w);
  const height = Math.round(h);
  if (width < unit * 2 || height < unit * 2) return;
  ctx.fillStyle = color;
  ctx.fillRect(left, top, width, unit);
  ctx.fillRect(left, top + height - unit, width, unit);
  ctx.fillRect(left, top, unit, height);
  ctx.fillRect(left + width - unit, top, unit, height);
}
